import { useState, useEffect, useRef, useCallback } from 'react';
import { Save, RotateCcw, Plus, X, ChevronDown, ChevronUp, BookOpen, Tag, Search, ArrowRight, CheckCircle, MessageSquare, Calendar, Edit2, Trash2, GripVertical, Mail, MapPin, AlertTriangle, Upload, Globe, FileText, Loader2, RefreshCw, ExternalLink, Lock, Plane } from '../../icons/wp-icons';
import toast from 'react-hot-toast';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { kiZentraleApi } from '../../api/kiZentraleApi';
import { voiceAssistantApi } from '../../api/voiceAssistantApi';
import { useAuth } from '../../context/AuthContext';
import Geschaeftszeiten from '../../pages/Geschaeftszeiten';
import TerminKategorienEditor from './TerminKategorienEditor';
import { JarvisSettings } from '../heykiki/JarvisAvatar';

function Collapsible({ isOpen, children }) {
  const [overflow, setOverflow] = useState(isOpen ? 'visible' : 'hidden');
  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          onAnimationStart={() => setOverflow('hidden')}
          onAnimationComplete={() => setOverflow('visible')}
          style={{ overflow }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ⚠️ KIKI-LEVEL UI-INVERSION (seit 2026-04-25)
// Display-Reihenfolge ist umgekehrt zur DB-Integer-Semantik. `value` ist der
// Backend-Integer (UNVERÄNDERT — alle 73+ Code-Vergleiche im Backend gehen weiter
// gegen 1=AUTOMATISCH, 2=BENACHRICHTIGUNG, 3=NUR_DATEN). `displayLevel` ist die
// Nummer, die der User sieht; jetzt wächst sie mit dem Autonomiegrad.
//   UI „Level 1" → value=3 (Nur Daten)        ← linke Position
//   UI „Level 2" → value=2 (Mit Bestätigung)  ← Mitte
//   UI „Level 3" → value=1 (Voll automatisch) ← rechte Position
// Doku: memory/feedback_kiki_level_ui_inversion.md

// Wissensbasis-Freitext-Schwellen (Zeichen):
//   < SOFT  : grün — alles okay
//   SOFT…   : gelbe Warnung, weil der Text bei jedem Turn im System-Prompt landet
//   HARD…   : orange Warnung, immer noch speicherbar
//   > MAX   : rot, Hard-Stop (Backend lehnt ebenfalls ab — kiZentraleRoutes.js)
const KNOWLEDGE_BASE_SOFT_LIMIT = 5000;
const KNOWLEDGE_BASE_HARD_LIMIT = 10000;
const KNOWLEDGE_BASE_MAX_LIMIT = 15000;

const AUTONOMY_LEVELS = [
  {
    value: 3,
    displayLevel: 1,
    label: 'Nur Daten sammeln',
    description: 'Der Agent sammelt nur Informationen und erstellt Anfragen. Keine automatischen Aktionen.',
    // HeyKiki-Variante ersetzt „Der Agent" durch „Kiki" — konsistent mit dem
    // Sprachgebrauch im HeyKiki-Portal (siehe übrige isHeykikiOrg-Texte unten).
    heykikiDescription: 'Kiki sammelt nur Informationen und erstellt Anfragen. Keine automatischen Aktionen.',
    image: '/kiki-level-3.png',
    // Standalone-Variante: neutrales, in der Org-Akzentfarbe einfärbbares SVG.
    iconSvg: '/kiki-icons/level3-nurdaten.svg?v=2',
  },
  {
    value: 2,
    displayLevel: 2,
    label: 'Mit Bestätigung',
    description: 'Termine und KVAs müssen vom Inhaber bestätigt werden, bevor sie dem Kunden zugesagt werden.',
    image: '/kiki-level-2.png',
    iconSvg: '/kiki-icons/level2-bestaetigung.svg?v=2',
  },
  {
    value: 1,
    displayLevel: 3,
    label: 'Voll automatisch',
    description: 'Termine werden direkt gebucht, KVAs automatisch erstellt und versendet.',
    image: '/kiki-level-1.png',
    iconSvg: '/kiki-icons/level1-auto.svg?v=2',
  },
];

// Partikel-Burst für HeyKiki-Autonomielevel-Klick. 14 Sparkles fliegen radial
// nach außen, Winkel leicht gejittert damit es nicht wie ein Zahnrad aussieht.
// Mix aus `star`-Sparkles (4-zackig, groß) und `dot`-Punkten (klein, für Füllung).
const KIKI_PARTICLES = Array.from({ length: 14 }).map((_, i) => {
  const baseAngle = (i / 14) * 360;
  const jitter = ((i * 37) % 20) - 10;       // -10..+10° pseudo-random
  const isStar = i % 3 === 0;                 // jeder 3. ist ein Sparkle
  return {
    angle: baseAngle + jitter,
    distance: isStar ? 75 + ((i * 13) % 20) : 55 + ((i * 17) % 25),
    size: isStar ? 10 + ((i * 7) % 4) : 4 + ((i * 11) % 3),
    delay: (i * 19) % 160,                    // 0..160ms staggered
    kind: isStar ? 'star' : 'dot',
  };
});

// ── Standalone-Icon-Karten ────────────────────────────────────────────────
// HeyKiki-Orgs nutzen die Kiki-Maskottchen-PNGs (eigene Buttons, unverändert).
// Standalone-Orgs bekommen dasselbe Karten-Layout, aber mit neutralen, in der
// Org-Akzentfarbe (--primary) einfärbbaren SVG-Icons aus /public/kiki-icons/.
//
// Einfärbung via CSS-Mask: das SVG liefert nur die Form (Alpha), die Farbe
// kommt aus `background-color`. Aktiv = --primary (= eingestellte Org-Farbe),
// inaktiv = neutrales Grau. Dadurch ist KEIN separates Aktiv/Inaktiv-Bild nötig.
function TintedKiIcon({ src, active }) {
  return (
    <span
      aria-hidden="true"
      className="relative block w-3/4 h-3/4 transition-colors duration-200"
      style={{
        backgroundColor: active ? 'var(--primary)' : '#9CA3AF',
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }}
    />
  );
}

// Gemeinsamer „Avatar-Bereich" der Standalone-Karten: Bling-Ringe, Akzent-Glow,
// Flash + Partikel-Burst (identisch zur HeyKiki-Variante) — nur mit getöntem
// Neutral-Icon statt Maskottchen-PNG.
function KiIconStage({ src, active }) {
  return (
    <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center flex-shrink-0">
      <span className="kiki-level-bling-ring pointer-events-none" aria-hidden="true" />
      <span className="kiki-level-bling-ring kiki-level-bling-ring-delayed pointer-events-none" aria-hidden="true" />
      <span
        aria-hidden="true"
        className={`absolute inset-[-20%] rounded-full pointer-events-none transition-opacity duration-300 ${
          active ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 55%, transparent) 0%, color-mix(in srgb, var(--primary) 25%, transparent) 40%, transparent 70%)',
          filter: 'blur(14px)',
        }}
      />
      <span className="kiki-level-flash pointer-events-none" aria-hidden="true" />
      {KIKI_PARTICLES.map((p, i) => (
        <span
          key={i}
          className={`kiki-particle kiki-particle-${p.kind} pointer-events-none`}
          style={{
            '--kiki-a': `${p.angle}deg`,
            '--kiki-d': `${p.distance}px`,
            '--kiki-size': `${p.size}px`,
            animationDelay: `${p.delay}ms`,
          }}
          aria-hidden="true"
        />
      ))}
      <TintedKiIcon src={src} active={active} />
    </div>
  );
}

// Standalone-An/Aus-Karte (Terminplanung, KVA, Preisauskunft). Spiegelt das
// HeyKiki-Button-Layout, nur mit neutralem Icon. `Lock` kommt aus dem Modul-
// Import oben.
function KiFeatureCard({ src, active, disabled, locked, bling, onClick, label, description, lockHint }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`kiki-level-btn relative w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
        active
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border bg-white hover:border-primary/40 opacity-80 hover:opacity-100'
      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${bling ? 'kiki-level-bling' : ''}`}
      aria-pressed={active}
      aria-label={`${label} ${active ? 'aktiv' : 'inaktiv'}${locked ? ' — gesperrt durch Autonomiestufe „Nur Daten sammeln"' : ''}`}
      title={locked ? lockHint : undefined}
    >
      <KiIconStage src={src} active={active} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold tracking-wide uppercase text-text-muted">{label}</span>
          {locked && <Lock className="w-3 h-3 text-text-muted" aria-hidden="true" />}
        </div>
        <div className={`mt-0.5 text-base font-semibold ${active ? 'text-primary' : 'text-text'}`}>
          {active ? 'Aktiv' : 'Inaktiv'}
        </div>
        <p className="mt-1 text-xs text-text-muted leading-snug">{description}</p>
      </div>
    </button>
  );
}

// Smart-Key-Derivation für die HeyKiki-Freitext-Pflichtfeld-Eingabe.
// Handwerker tippen nur ein Label („Dachlänge", „E-Mail", „Anliegen") — wir
// leiten daraus automatisch einen Key ab. Bekannte Stammdaten-Labels werden
// auf die Standard-Keys gemappt (siehe DEFAULT_REQUIRED_FIELDS in
// onboardingController.js), damit der Server-Hard-Gate in
// heykiki/services/toolExecuteService.js weiter greift. Unbekannte Labels
// werden slugifiziert (Umlaute + Nicht-Alphanumerisches → `_`) und sind dann
// Prompt-only — kein serverseitiges Enforcement, nur Instruktion an Kiki.
const STANDARD_PFLICHTFELD_ALIASES = {
  'name': 'name', 'kundenname': 'name',
  'telefon': 'phone', 'telefonnummer': 'phone', 'rufnummer': 'phone',
  'handy': 'phone', 'mobilnummer': 'phone',
  'adresse': 'address', 'anschrift': 'address',
  'email': 'email', 'e-mail': 'email', 'mail': 'email',
  'emailadresse': 'email', 'e-mailadresse': 'email', 'e-mail-adresse': 'email',
  'anliegen': 'concern', 'problem': 'concern', 'anliegen/problem': 'concern',
  'kundennummer': 'kundennummer', 'kundennr': 'kundennummer',
  'kunden-nr': 'kundennummer', 'kunden-nummer': 'kundennummer',
};
function deriveFeldKeyFromLabel(label) {
  const norm = (label || '').toLowerCase().trim();
  if (!norm) return '';
  if (STANDARD_PFLICHTFELD_ALIASES[norm]) return STANDARD_PFLICHTFELD_ALIASES[norm];
  return norm
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export default function KiEinstellungen({ readOnly }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const [settings, setSettings] = useState(null);
  const [pflichtfelder, setPflichtfelder] = useState(null);
  const [leistungen, setLeistungen] = useState(null);
  const [brancheData, setBrancheData] = useState(null);
  const [themen, setThemen] = useState([]);
  // Voice-Settings werden für HeyKiki-Orgs zusätzlich geladen, weil die
  // Notdienst-Nummer (`emergencyForwardingNumber`) auf VoiceAssistantSettings
  // sitzt und im Notdienst-Container hier gepflegt wird (Migration aus dem
  // TelefonAgent-Tab am 2026-05-09 — Lucas-Feedback Husmann & Dreier).
  const [voiceSettings, setVoiceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blingLevel, setBlingLevel] = useState(null);
  const [blingKva, setBlingKva] = useState(false);
  const [blingPreis, setBlingPreis] = useState(false);
  const [blingTermin, setBlingTermin] = useState(false);
  // Anzahl READY-Wissensquellen für die Wissensbasis-Vorschau im Section-Header.
  // Wird vom WissensquellenSection-Kind via onCountChange-Callback aktualisiert,
  // damit die Vorschau nach Upload/Delete sofort stimmt.
  const [wissensQuellenReadyCount, setWissensQuellenReadyCount] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    autonomie: true,
    pflichtfelder: true,
    themen: true,
    geschaeftszeiten: false,
    terminplanung: true,
    urlaubsmodus: false,
    preisauskunft: false,
    branche: false,
    leistungen: false,
    kva: true,
    notdienst: false,
    emailBenachrichtigungen: false,
    wissensbasis: false,
  });
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');

  // Themen State
  const [expandedThemen, setExpandedThemen] = useState({});
  const [showNewThemaForm, setShowNewThemaForm] = useState(false);
  const [newThema, setNewThema] = useState({ name: '', beschreibung: '', keywords: '' });
  const [editingThema, setEditingThema] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [settingsData, pflichtfelderData, leistungenData, branche, themenData, vasData] = await Promise.all([
        kiZentraleApi.getSettings(),
        kiZentraleApi.getPflichtfelder(),
        kiZentraleApi.getLeistungen(),
        kiZentraleApi.getBranche(),
        kiZentraleApi.getThemen().catch(() => []),
        // VoiceAssistantSettings nur für HeyKiki laden — Standalone hat den
        // Notdienst-Block weiterhin im TelefonAgent-Tab.
        isHeykikiOrg ? voiceAssistantApi.getSettings().catch(() => null) : Promise.resolve(null),
      ]);
      setSettings(settingsData);
      setPflichtfelder(pflichtfelderData);
      setLeistungen(leistungenData);
      setBrancheData(branche);
      setThemen(themenData);
      setVoiceSettings(vasData);
    } catch (err) {
      console.error('KI Einstellungen laden fehlgeschlagen:', err);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  // Save für die Notdienst-Nummer — landet auf VoiceAssistantSettings, NICHT
  // auf KiAgentSettings. Wir spreaden die existierenden voiceSettings, damit
  // der bestehende PUT-Endpoint andere Felder nicht auf Defaults zurücksetzt.
  const handleSaveEmergencyNumber = async (number) => {
    if (readOnly) return;
    try {
      const result = await voiceAssistantApi.updateSettings({
        ...(voiceSettings || {}),
        emergencyForwardingNumber: number || null,
      });
      setVoiceSettings(result);
      toast.success('Notdienst-Nummer gespeichert');
    } catch (err) {
      console.error('Notdienst-Nummer speichern fehlgeschlagen:', err);
      toast.error('Fehler beim Speichern der Notdienst-Nummer');
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveSettings = async (updates) => {
    if (readOnly) return;
    setSaving(true);
    try {
      const result = await kiZentraleApi.updateSettings(updates);
      setSettings(prev => ({ ...prev, ...result }));
      toast.success('Einstellungen gespeichert');
    } catch (err) {
      console.error('Speichern fehlgeschlagen:', err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePflichtfelder = async (felder) => {
    if (readOnly) return;
    setSaving(true);
    try {
      await kiZentraleApi.updatePflichtfelder(felder);
      setPflichtfelder(prev => ({ ...prev, pflichtfelder: felder }));
      toast.success('Pflichtfelder gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern der Pflichtfelder');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPflichtfelder = async () => {
    if (readOnly) return;
    try {
      const result = await kiZentraleApi.resetPflichtfelder();
      setPflichtfelder(prev => ({ ...prev, pflichtfelder: result.pflichtfelder }));
      toast.success('Pflichtfelder auf Standard zurückgesetzt');
    } catch (err) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const handleSaveLeistungen = async (next) => {
    if (readOnly) return;
    const payload = next || {
      angebotenLeistungen: leistungen?.angebotenLeistungen,
      nichtAngeboten: leistungen?.nichtAngeboten,
    };
    setSaving(true);
    try {
      await kiZentraleApi.updateLeistungen(payload);
      toast.success('Leistungen gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern der Leistungen');
    } finally {
      setSaving(false);
    }
  };

  // === Themen Handlers ===
  const handleCreateThema = async () => {
    if (readOnly || !newThema.name.trim()) return;
    try {
      const keywords = newThema.keywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
      const created = await kiZentraleApi.createThema({
        name: newThema.name.trim(),
        beschreibung: newThema.beschreibung.trim() || null,
        keywords,
      });
      setThemen(prev => [...prev, created]);
      setNewThema({ name: '', beschreibung: '', keywords: '' });
      setShowNewThemaForm(false);
      setExpandedThemen(prev => ({ ...prev, [created.id]: true }));
      toast.success(`Thema "${created.name}" erstellt`);
    } catch (err) {
      toast.error('Fehler beim Erstellen des Themas');
    }
  };

  const handleUpdateThema = async (id) => {
    if (readOnly || !editingThema) return;
    try {
      const keywords = typeof editingThema.keywords === 'string'
        ? editingThema.keywords.split(',').map(k => k.trim()).filter(Boolean)
        : editingThema.keywords || [];
      const updated = await kiZentraleApi.updateThema(id, {
        name: editingThema.name,
        beschreibung: editingThema.beschreibung,
        keywords,
      });
      setThemen(prev => prev.map(t => t.id === id ? updated : t));
      setEditingThema(null);
      toast.success('Thema aktualisiert');
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteThema = async (id) => {
    if (readOnly) return;
    try {
      await kiZentraleApi.deleteThema(id);
      setThemen(prev => prev.filter(t => t.id !== id));
      setDeleteConfirm(null);
      toast.success('Thema gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSaveThemenFelder = async (themaId, felder) => {
    if (readOnly) return;
    try {
      const updated = await kiZentraleApi.updateThemenPflichtfelder(themaId, felder);
      setThemen(prev => prev.map(t =>
        t.id === themaId ? { ...t, pflichtfelder: updated } : t
      ));
      toast.success('Themen-Pflichtfelder gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern der Themen-Pflichtfelder');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!settings) return null;

  const SectionHeader = ({ id, title, children }) => (
    <div
      className="flex items-center justify-between cursor-pointer py-3"
      onClick={() => toggleSection(id)}
    >
      <h3 className="text-base font-semibold text-text flex items-center gap-2">
        {title}
        {children}
      </h3>
      {expandedSections[id] ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
    </div>
  );

  const currentPflichtfelder = pflichtfelder?.pflichtfelder || {};

  // Im „Nur Daten sammeln"-Modus (DB-Integer `autonomyLevel === 3`, in der UI
  // als „Level 1" angezeigt — siehe AUTONOMY_LEVELS-Header oben) sind
  // KVA-Erstellung und Terminplanung im ElevenLabs-/VAPI-Agenten ohnehin gated
  // (Backend-Vergleich `autonomyLevel <= 2`). Wir visualisieren das in der UI:
  // Buttons gehen auf Inaktiv, kriegen ein Schloss-Icon und sind nicht klickbar.
  // Der gespeicherte Toggle-Wert bleibt erhalten — beim Zurückwechsel auf
  // UI-Level 2 oder 3 (DB 2/1) ist die ursprüngliche User-Wahl wieder aktiv.
  // Der Variablenname `isLevel3` bezieht sich auf den DB-Integer, NICHT auf das
  // UI-Label.
  const isLevel3 = settings.autonomyLevel === 3;
  const kvaEffectivelyAktiv = settings.kvaErstellungAktiv && !isLevel3;
  const terminEffectivelyAktiv = (settings.terminBuchungAktiv !== false) && !isLevel3;

  // Branche/Gewerk-Block — HeyKiki nennt es „Gewerk" und zeigt es oberhalb
  // des Kiki-Levels, Standalone weiter als „Branche" in der alten Position.
  // Im eingeklappten Zustand zeigen wir das aktuell gewählte Gewerk als
  // Badge neben dem Titel, damit man es nicht aufklappen muss um zu sehen
  // was gesetzt ist.
  const selectedBrancheLabel = settings.branche === 'sonstiges'
    ? (settings.brancheCustom?.trim() || 'Sonstiges Handwerk')
    : brancheData?.allBranchen?.find(b => b.value === settings.branche)?.label;
  const brancheBlock = (
    <div className="bg-surface rounded-card shadow-card p-5">
      <SectionHeader id="branche" title={isHeykikiOrg ? 'Gewerk' : 'Branche'}>
        {selectedBrancheLabel && !expandedSections.branche && (
          <span className="text-sm font-normal text-text-muted">· {selectedBrancheLabel}</span>
        )}
      </SectionHeader>
      <Collapsible isOpen={!!(expandedSections.branche && brancheData)}>
        <div className="mt-2 space-y-3">
          <select
            value={settings.branche || ''}
            onChange={(e) => {
              const val = e.target.value;
              setSettings(prev => ({ ...prev, branche: val }));
              handleSaveSettings({ branche: val });
            }}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
          >
            <option value="">{isHeykikiOrg ? 'Gewerk wählen...' : 'Branche wählen...'}</option>
            {brancheData?.allBranchen?.map(b => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
          {settings.branche === 'sonstiges' && (
            <input
              type="text"
              placeholder={isHeykikiOrg ? 'Gewerk beschreiben...' : 'Branche beschreiben...'}
              value={settings.brancheCustom || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, brancheCustom: e.target.value }))}
              onBlur={() => handleSaveSettings({ brancheCustom: settings.brancheCustom })}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          )}
        </div>
      </Collapsible>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Jarvis-Beta-Settings — rendert sich selbst nur für whitelisted HeyKiki-Subs */}
      <JarvisSettings />

      {/* Gewerk — bei HeyKiki-Orgs oberhalb des Kiki-Levels. */}
      {isHeykikiOrg && brancheBlock}

      {/* Autonomiestufe — HeyKiki spricht hier von „Kiki-Level", Standalone weiter von Agenten. */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="autonomie" title={isHeykikiOrg ? 'Kiki-Level' : 'Autonomiestufe'}>
          {!isHeykikiOrg && (
            <span className="text-xs font-normal text-text-muted bg-primary/10 px-2 py-0.5 rounded-full">Alle Agenten</span>
          )}
          {!expandedSections.autonomie && (() => {
            const cur = AUTONOMY_LEVELS.find(l => l.value === settings.autonomyLevel);
            return cur ? (
              <span className="text-sm font-normal text-text-muted">· Level {cur.displayLevel} — {cur.label}</span>
            ) : null;
          })()}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.autonomie}>
          <div className="space-y-3 mt-2">
            <p className="text-sm text-text-muted mb-4">
              {isHeykikiOrg
                ? 'Bestimmt, wie selbstständig Kiki arbeitet.'
                : 'Bestimmt, wie selbstständig die KI-Agenten handeln. Diese Einstellung gilt für Telefon, WhatsApp und E-Mail.'}
            </p>
            {isHeykikiOrg ? (
              // HEYKIKI: Avatar-Buttons mit Bling-Pulse in Akzentfarbe der Org.
              // Akzentfarbe kommt aus `--primary` (BrandingLoader.jsx:99, per Org gesetzt).
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {AUTONOMY_LEVELS.map((level) => {
                  const isSelected = settings.autonomyLevel === level.value;
                  const isBling = blingLevel === level.value;
                  return (
                    <button
                      key={level.value}
                      type="button"
                      disabled={readOnly}
                      onClick={() => {
                        if (readOnly) return;
                        setSettings(prev => ({ ...prev, autonomyLevel: level.value }));
                        handleSaveSettings({ autonomyLevel: level.value });
                        setBlingLevel(level.value);
                        // Dauer muss zur längsten Kind-Animation passen:
                        // kiki-particle-burst 800ms + staggerDelay bis 150ms = ~950ms.
                        setTimeout(() => setBlingLevel(null), 1000);
                      }}
                      className={`kiki-level-btn relative flex flex-col items-center text-center p-4 rounded-2xl border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-md scale-[1.02]'
                          : 'border-border bg-white hover:border-primary/40 opacity-70 hover:opacity-100'
                      } ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'} ${isBling ? 'kiki-level-bling' : ''}`}
                      aria-pressed={isSelected}
                      aria-label={`Level ${level.displayLevel}: ${level.label}`}
                    >
                      <div className="relative w-32 h-32 sm:w-36 sm:h-36 flex items-center justify-center">
                        {/* Bling-Ringe pulsen vom Avatar aus, nicht vom Button-Rand,
                            damit sie auch bei vollbreiten Buttons innerhalb des
                            Avatar-Bereichs bleiben (sonst Cut durch overflow-x
                            der Page-Layouts). */}
                        <span className="kiki-level-bling-ring pointer-events-none" aria-hidden="true" />
                        <span className="kiki-level-bling-ring kiki-level-bling-ring-delayed pointer-events-none" aria-hidden="true" />
                        {/* Aktivitäts-Glow — weicher radialer Schein in der Akzentfarbe,
                            nur sichtbar beim aktiven Level. Liegt hinter dem Avatar. */}
                        <span
                          aria-hidden="true"
                          className={`absolute inset-[-20%] rounded-full pointer-events-none transition-opacity duration-300 ${
                            isSelected ? 'opacity-100' : 'opacity-0'
                          }`}
                          style={{
                            background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 55%, transparent) 0%, color-mix(in srgb, var(--primary) 25%, transparent) 40%, transparent 70%)',
                            filter: 'blur(14px)',
                          }}
                        />
                        {/* Zentraler Flash — nur während Bling, radialer Gradient in Akzentfarbe. */}
                        <span className="kiki-level-flash pointer-events-none" aria-hidden="true" />
                        {/* Partikel-Burst — 14 Sparkles fliegen radial nach außen.
                            Winkel/Distanz/Delay per CSS-Variable gesetzt, Keyframe nutzt cos/sin(). */}
                        {KIKI_PARTICLES.map((p, i) => (
                          <span
                            key={i}
                            className={`kiki-particle kiki-particle-${p.kind} pointer-events-none`}
                            style={{
                              '--kiki-a': `${p.angle}deg`,
                              '--kiki-d': `${p.distance}px`,
                              '--kiki-size': `${p.size}px`,
                              animationDelay: `${p.delay}ms`
                            }}
                            aria-hidden="true"
                          />
                        ))}
                        <img
                          src={level.image}
                          alt=""
                          className={`relative w-full h-full object-contain transition-all duration-200 ${
                            isSelected ? '' : 'grayscale contrast-75'
                          }`}
                          draggable={false}
                        />
                      </div>
                      <span className="mt-2 text-xs font-semibold tracking-wide uppercase text-text-muted">
                        Level {level.displayLevel}
                      </span>
                      <span className={`mt-0.5 text-sm font-semibold ${isSelected ? 'text-primary' : 'text-text'}`}>
                        {level.label}
                      </span>
                      <span className="mt-1 text-xs text-text-muted leading-snug">
                        {level.heykikiDescription || level.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              // STANDALONE: gleiche Icon-Karten wie HeyKiki, aber mit neutralem,
              // in der Org-Akzentfarbe (--primary) getöntem SVG statt Maskottchen.
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {AUTONOMY_LEVELS.map((level) => {
                  const isSelected = settings.autonomyLevel === level.value;
                  const isBling = blingLevel === level.value;
                  return (
                    <button
                      key={level.value}
                      type="button"
                      disabled={readOnly}
                      onClick={() => {
                        if (readOnly) return;
                        setSettings(prev => ({ ...prev, autonomyLevel: level.value }));
                        handleSaveSettings({ autonomyLevel: level.value });
                        setBlingLevel(level.value);
                        setTimeout(() => setBlingLevel(null), 1000);
                      }}
                      className={`kiki-level-btn relative flex flex-col items-center text-center p-4 rounded-2xl border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-md scale-[1.02]'
                          : 'border-border bg-white hover:border-primary/40 opacity-70 hover:opacity-100'
                      } ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'} ${isBling ? 'kiki-level-bling' : ''}`}
                      aria-pressed={isSelected}
                      aria-label={`Level ${level.displayLevel}: ${level.label}`}
                    >
                      <KiIconStage src={level.iconSvg} active={isSelected} />
                      <span className="mt-2 text-xs font-semibold tracking-wide uppercase text-text-muted">
                        Level {level.displayLevel}
                      </span>
                      <span className={`mt-0.5 text-sm font-semibold ${isSelected ? 'text-primary' : 'text-text'}`}>
                        {level.label}
                      </span>
                      <span className="mt-1 text-xs text-text-muted leading-snug">
                        {level.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Collapsible>
      </div>

      {/* Allgemeine Pflichtfelder — für HeyKiki: „Kiki Leitfaden". */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="pflichtfelder" title={isHeykikiOrg ? 'Kiki Leitfaden' : 'Leitfaden'}>
          {!isHeykikiOrg && (
            <span className="text-xs font-normal text-text-muted bg-primary/10 px-2 py-0.5 rounded-full">Alle Agenten</span>
          )}
          {!expandedSections.pflichtfelder && Object.keys(currentPflichtfelder).length > 0 && (
            <span className="text-sm font-normal text-text-muted">
              · {Object.keys(currentPflichtfelder).length} {Object.keys(currentPflichtfelder).length === 1 ? 'Feld' : 'Felder'}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.pflichtfelder}>
          <div className="mt-2">
            <p className="text-sm text-text-muted mb-4">
              {isHeykikiOrg
                ? <>Felder, die <strong>Kiki</strong> bei jeder Anfrage abfragen muss, unabhängig vom Thema. Die hier festgelegte Reihenfolge entspricht der Reihenfolge, in der Kiki die Daten im Gespräch abfragt — per Drag &amp; Drop anpassbar.</>
                : <>Der <strong>Leitfaden</strong>: Felder, die die KI-Agenten (WhatsApp, E-Mail, Telefon) bei <strong>jeder</strong> Anfrage abfragen — unabhängig vom Thema. Die Reihenfolge entspricht der Abfrage-Reihenfolge (in WhatsApp/E-Mail werden alle fehlenden Felder in einer Nachricht erfragt). Per Drag &amp; Drop anpassbar.</>}
            </p>
            <HeyKikiPflichtfelderEditor
              pflichtfelder={currentPflichtfelder}
              onChange={handleSavePflichtfelder}
              readOnly={readOnly}
              isHeykikiOrg={isHeykikiOrg}
            />

            {/* Feld hinzufügen */}
            {!readOnly && (
              // Leitfaden (beide Org-Typen): nur Freitext-Label — Key wird aus Label abgeleitet
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3">
                <input
                  type="text"
                  placeholder="Pflichtfeld (z.B. Dachlänge, E-Mail, Glasart)"
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-border rounded-lg"
                />
                <button
                  onClick={() => {
                    const label = newFieldLabel.trim();
                    if (!label) return;
                    const key = deriveFeldKeyFromLabel(label);
                    if (!key) return;
                    const updated = { ...currentPflichtfelder };
                    updated[key] = { label, pflicht: true };
                    handleSavePflichtfelder(updated);
                    setNewFieldLabel('');
                  }}
                  className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 self-end sm:self-auto"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Reset Button */}
            {!readOnly && (
              <button
                onClick={handleResetPflichtfelder}
                className="flex items-center gap-2 mt-3 text-sm text-text-muted hover:text-text"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Auf Standard zurücksetzen
              </button>
            )}
          </div>
        </Collapsible>
      </div>

      {/* Terminplanung — direkt unter Kiki Leitfaden, weil beides gemeinsam
          das Verhalten von Kiki im Gespräch steuert (Datenaufnahme + Termin-
          Buchung in Schritt 2/3). */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="terminplanung" title="Terminplanung">
          {!isHeykikiOrg && (
            <span className="text-xs font-normal text-text-muted bg-primary/10 px-2 py-0.5 rounded-full">Alle Agenten</span>
          )}
          {!expandedSections.terminplanung && (
            <span className="text-sm font-normal text-text-muted">
              · {isLevel3 ? 'Gesperrt' : (terminEffectivelyAktiv ? 'Aktiv' : 'Inaktiv')}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.terminplanung}>
          <div className="mt-2 space-y-4">
            {/* Aktiv/Inaktiv-Toggle — gleiches Muster wie KVA-Automatisierung
                und Preisauskunft. Bei Inaktiv fallen im ElevenLabs-Agent
                bookAppointment/getAvailableAppointments/requestCancellation/
                requestAppointmentChange weg, und Schritt 3 im Leitfaden
                wird durch ein „Team meldet sich"-Outro ersetzt
                (siehe agentConfigService.terminAktiv). */}
            {isHeykikiOrg ? (
              <button
                type="button"
                disabled={readOnly || isLevel3}
                onClick={() => {
                  if (readOnly || isLevel3) return;
                  const newVal = !(settings.terminBuchungAktiv !== false);
                  setSettings(prev => ({ ...prev, terminBuchungAktiv: newVal }));
                  handleSaveSettings({ terminBuchungAktiv: newVal });
                  if (newVal) {
                    setBlingTermin(true);
                    setTimeout(() => setBlingTermin(false), 1000);
                  }
                }}
                className={`kiki-level-btn relative w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                  terminEffectivelyAktiv
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-white hover:border-primary/40 opacity-80 hover:opacity-100'
                } ${(readOnly || isLevel3) ? 'cursor-not-allowed' : 'cursor-pointer'} ${blingTermin ? 'kiki-level-bling' : ''}`}
                aria-pressed={terminEffectivelyAktiv}
                aria-label={`Terminplanung ${terminEffectivelyAktiv ? 'aktiv' : 'inaktiv'}${isLevel3 ? (isHeykikiOrg ? ' — gesperrt durch Kiki-Level 1' : ' — gesperrt durch Autonomiestufe „Nur Daten sammeln"') : ''}`}
                title={isLevel3 ? (isHeykikiOrg ? 'Im Kiki-Level „Nur Daten sammeln" nicht verfügbar — wechsle auf Level 2 oder 3 zum Aktivieren.' : 'In der Autonomiestufe „Nur Daten sammeln" nicht verfügbar — wechsle auf Level 2 oder 3 zum Aktivieren.') : undefined}
              >
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center flex-shrink-0">
                  {/* Bling-Ringe pulsen vom Avatar aus (statt vom ganzen Button),
                      damit sie auch bei breiten Pillen-Buttons innerhalb des
                      Avatar-Bereichs bleiben und nicht vom Page-Layout
                      (overflow-x: hidden) gekappt werden. */}
                  <span className="kiki-level-bling-ring pointer-events-none" aria-hidden="true" />
                  <span className="kiki-level-bling-ring kiki-level-bling-ring-delayed pointer-events-none" aria-hidden="true" />
                  <span
                    aria-hidden="true"
                    className={`absolute inset-[-20%] rounded-full pointer-events-none transition-opacity duration-300 ${
                      terminEffectivelyAktiv ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 55%, transparent) 0%, color-mix(in srgb, var(--primary) 25%, transparent) 40%, transparent 70%)',
                      filter: 'blur(14px)',
                    }}
                  />
                  <span className="kiki-level-flash pointer-events-none" aria-hidden="true" />
                  {KIKI_PARTICLES.map((p, i) => (
                    <span
                      key={i}
                      className={`kiki-particle kiki-particle-${p.kind} pointer-events-none`}
                      style={{
                        '--kiki-a': `${p.angle}deg`,
                        '--kiki-d': `${p.distance}px`,
                        '--kiki-size': `${p.size}px`,
                        animationDelay: `${p.delay}ms`
                      }}
                      aria-hidden="true"
                    />
                  ))}
                  <img
                    src="/termin_button.png"
                    alt=""
                    className={`relative w-full h-full object-contain transition-all duration-200 ${
                      terminEffectivelyAktiv ? '' : 'grayscale contrast-75'
                    }`}
                    draggable={false}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold tracking-wide uppercase text-text-muted">
                      Terminplanung
                    </span>
                    {isLevel3 && <Lock className="w-3 h-3 text-text-muted" aria-hidden="true" />}
                  </div>
                  <div className={`mt-0.5 text-base font-semibold ${terminEffectivelyAktiv ? 'text-primary' : 'text-text'}`}>
                    {terminEffectivelyAktiv ? 'Aktiv' : 'Inaktiv'}
                  </div>
                  <p className="mt-1 text-xs text-text-muted leading-snug">
                    {isLevel3
                      ? 'Im Kiki-Level „Nur Daten sammeln" nicht verfügbar — Kiki bucht in diesem Modus generell keine Termine.'
                      : 'Wenn aktiviert, kann Kiki im Gespräch Termine vorschlagen und buchen. Bei Inaktiv erstellt Kiki nur Anfragen — das Team meldet sich für die Terminierung zurück.'}
                  </p>
                </div>
              </button>
            ) : (
              <KiFeatureCard
                src="/kiki-icons/termin.svg?v=2"
                active={terminEffectivelyAktiv}
                disabled={readOnly || isLevel3}
                locked={isLevel3}
                bling={blingTermin}
                label="Terminplanung"
                lockHint={'In der Autonomiestufe „Nur Daten sammeln" nicht verfügbar — wechsle auf Level 2 oder 3 zum Aktivieren.'}
                description={isLevel3
                  ? 'Im Modus „Nur Daten sammeln" nicht verfügbar — es werden generell keine Termine gebucht.'
                  : 'Wenn aktiviert, können die Agenten Termine vorschlagen und buchen. Bei Inaktiv werden nur Anfragen erstellt — das Team meldet sich zur Terminierung.'}
                onClick={() => {
                  if (readOnly || isLevel3) return;
                  const newVal = !(settings.terminBuchungAktiv !== false);
                  setSettings(prev => ({ ...prev, terminBuchungAktiv: newVal }));
                  handleSaveSettings({ terminBuchungAktiv: newVal });
                  if (newVal) {
                    setBlingTermin(true);
                    setTimeout(() => setBlingTermin(false), 1000);
                  }
                }}
              />
            )}

            {/* Pufferzeit / Max-Termine / Parallel-Kapazität nur sichtbar
                wenn Terminplanung effektiv aktiv — bei Inaktiv ODER im UI-Level 1
                („Nur Daten", DB-Integer 3) haben diese Werte keine Wirkung. */}
            {terminEffectivelyAktiv && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-border">
                <label className="text-sm font-medium text-text block mb-1">Pufferzeit zwischen Terminen</label>
                <p className="text-xs text-text-muted mb-2">Mindestabstand in Minuten zwischen zwei Terminen (z.B. für Anfahrt).</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={settings.standardPufferzeit ?? ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, standardPufferzeit: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    onBlur={() => handleSaveSettings({ standardPufferzeit: parseInt(settings.standardPufferzeit) || 15 })}
                    disabled={readOnly}
                    className="w-24 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <span className="text-sm text-text-muted">Minuten</span>
                </div>
              </div>
              <div className="p-4 rounded-lg border border-border">
                <label className="text-sm font-medium text-text block mb-1">Maximale Termine pro Tag</label>
                <p className="text-xs text-text-muted mb-2">{isHeykikiOrg ? 'Kiki bietet keine weiteren Termine an, wenn das Limit erreicht ist.' : 'Agenten bieten keine weiteren Termine an, wenn das Limit erreicht ist.'}</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={settings.maxTermineProTag ?? ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, maxTermineProTag: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    onBlur={() => handleSaveSettings({ maxTermineProTag: parseInt(settings.maxTermineProTag) || 8 })}
                    disabled={readOnly}
                    className="w-24 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <span className="text-sm text-text-muted">Termine</span>
                </div>
              </div>
              <div className="p-4 rounded-lg border border-border sm:col-span-2">
                <label className="text-sm font-medium text-text block mb-1">Parallele Termine pro Zeitslot</label>
                <p className="text-xs text-text-muted mb-2">
                  Wie viele Termine können gleichzeitig laufen? Entspricht der Anzahl Mitarbeiter, die parallel unterwegs sein können.
                  Bei <strong>1</strong> wird ein Slot als belegt markiert, sobald ein Termin läuft.
                  Bei z.B. <strong>3</strong> bleibt der Slot verfügbar, bis 3 parallele Termine gebucht sind.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={settings.parallelKapazitaet ?? ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, parallelKapazitaet: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    onBlur={() => handleSaveSettings({ parallelKapazitaet: Math.max(1, parseInt(settings.parallelKapazitaet) || 1) })}
                    disabled={readOnly}
                    className="w-24 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <span className="text-sm text-text-muted">Termine gleichzeitig</span>
                </div>
              </div>
              <div className="p-4 rounded-lg border border-border sm:col-span-2">
                <label className="text-sm font-medium text-text block mb-1">Vorlaufzeit für Terminbuchung</label>
                <p className="text-xs text-text-muted mb-2">
                  Wie viele Tage müssen zwischen Anruf und Termin mindestens liegen.
                  Mit <strong>0</strong> sind Same-Day-Termine erlaubt (klassisches Verhalten).
                  Mit <strong>1</strong> sind erst Termine ab dem nächsten Tag ab Geschäftsöffnung buchbar — die KI bietet heute keine Slots mehr an.
                  Mit „nur Werktage" zählen Sa/So nicht (Freitag-Anruf + 1 Tag = Montag).
                  Optional: eine <strong>Uhrzeit</strong> hebt den frühesten Slot am erstmöglichen Tag an
                  (z.B. 1 Tag + 13:00 → Folgetag erst ab 13 Uhr buchbar, davor keine Slots).
                </p>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <input
                    type="number"
                    min={0}
                    max={30}
                    value={settings.terminVorlaufTage ?? ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, terminVorlaufTage: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    onBlur={() => handleSaveSettings({ terminVorlaufTage: Math.max(0, Math.min(30, parseInt(settings.terminVorlaufTage) || 0)) })}
                    disabled={readOnly}
                    className="w-24 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <span className="text-sm text-text-muted">Tage Vorlauf</span>
                  <span className="text-sm text-text-muted ml-2">ab</span>
                  <input
                    type="time"
                    value={settings.terminVorlaufUhrzeit ?? ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, terminVorlaufUhrzeit: e.target.value || null }))}
                    onBlur={() => handleSaveSettings({ terminVorlaufUhrzeit: settings.terminVorlaufUhrzeit || null })}
                    disabled={readOnly || !(settings.terminVorlaufTage > 0)}
                    className="w-28 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-surface-muted disabled:text-text-muted"
                  />
                  <span className="text-sm text-text-muted">Uhr (optional)</span>
                  {settings.terminVorlaufUhrzeit && !readOnly && (
                    <button
                      type="button"
                      onClick={() => {
                        setSettings(prev => ({ ...prev, terminVorlaufUhrzeit: null }));
                        handleSaveSettings({ terminVorlaufUhrzeit: null });
                      }}
                      className="text-xs text-text-muted underline ml-1"
                    >
                      Uhrzeit löschen
                    </button>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.terminVorlaufNurWerktage}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setSettings(prev => ({ ...prev, terminVorlaufNurWerktage: v }));
                      handleSaveSettings({ terminVorlaufNurWerktage: v });
                    }}
                    disabled={readOnly}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm">Nur Werktage zählen (Sa/So überspringen)</span>
                </label>
              </div>
            </div>
            )}
            {/* Terminkategorien — pro Org definierbar, Default-Dauer + Default-
                Mitarbeiter werden automatisch übernommen. KI-Agent referenziert
                sie im System-Prompt und gibt den Namen beim bookAppointment-
                Aufruf als `kategorie`-Parameter mit. */}
            {terminEffectivelyAktiv && (
              <div className="mt-4 p-4 rounded-lg border border-border">
                <TerminKategorienEditor
                  readOnly={readOnly}
                  isHeykikiOrg={isHeykikiOrg}
                  filterAktiv={!!settings.terminBuchungKategorienFilterAktiv}
                  onFilterToggle={(v) => {
                    setSettings(prev => ({ ...prev, terminBuchungKategorienFilterAktiv: v }));
                    handleSaveSettings({ terminBuchungKategorienFilterAktiv: v });
                  }}
                />
              </div>
            )}
          </div>
        </Collapsible>
      </div>

      {/* KVA-Einstellungen */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="kva" title="Kostenvoranschlag-Automatisierung">
          {!expandedSections.kva && (
            <span className="text-sm font-normal text-text-muted">
              · {isLevel3 ? 'Gesperrt' : (kvaEffectivelyAktiv ? 'Aktiv' : 'Inaktiv')}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.kva}>
          <div className="mt-2 space-y-3">
            {isHeykikiOrg ? (
              <button
                type="button"
                disabled={readOnly || isLevel3}
                onClick={() => {
                  if (readOnly || isLevel3) return;
                  const newVal = !settings.kvaErstellungAktiv;
                  setSettings(prev => ({ ...prev, kvaErstellungAktiv: newVal }));
                  handleSaveSettings({ kvaErstellungAktiv: newVal });
                  if (newVal) {
                    setBlingKva(true);
                    setTimeout(() => setBlingKva(false), 1000);
                  }
                }}
                className={`kiki-level-btn relative w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                  kvaEffectivelyAktiv
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-white hover:border-primary/40 opacity-80 hover:opacity-100'
                } ${(readOnly || isLevel3) ? 'cursor-not-allowed' : 'cursor-pointer'} ${blingKva ? 'kiki-level-bling' : ''}`}
                aria-pressed={kvaEffectivelyAktiv}
                aria-label={`KVA-Automatisierung ${kvaEffectivelyAktiv ? 'aktiv' : 'inaktiv'}${isLevel3 ? (isHeykikiOrg ? ' — gesperrt durch Kiki-Level 1' : ' — gesperrt durch Autonomiestufe „Nur Daten sammeln"') : ''}`}
                title={isLevel3 ? (isHeykikiOrg ? 'Im Kiki-Level „Nur Daten sammeln" nicht verfügbar — wechsle auf Level 2 oder 3 zum Aktivieren.' : 'In der Autonomiestufe „Nur Daten sammeln" nicht verfügbar — wechsle auf Level 2 oder 3 zum Aktivieren.') : undefined}
              >
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center flex-shrink-0">
                  {/* Bling-Ringe pulsen vom Avatar aus (statt vom ganzen Button),
                      damit sie auch bei breiten Pillen-Buttons innerhalb des
                      Avatar-Bereichs bleiben und nicht vom Page-Layout
                      (overflow-x: hidden) gekappt werden. */}
                  <span className="kiki-level-bling-ring pointer-events-none" aria-hidden="true" />
                  <span className="kiki-level-bling-ring kiki-level-bling-ring-delayed pointer-events-none" aria-hidden="true" />
                  <span
                    aria-hidden="true"
                    className={`absolute inset-[-20%] rounded-full pointer-events-none transition-opacity duration-300 ${
                      kvaEffectivelyAktiv ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 55%, transparent) 0%, color-mix(in srgb, var(--primary) 25%, transparent) 40%, transparent 70%)',
                      filter: 'blur(14px)',
                    }}
                  />
                  <span className="kiki-level-flash pointer-events-none" aria-hidden="true" />
                  {KIKI_PARTICLES.map((p, i) => (
                    <span
                      key={i}
                      className={`kiki-particle kiki-particle-${p.kind} pointer-events-none`}
                      style={{
                        '--kiki-a': `${p.angle}deg`,
                        '--kiki-d': `${p.distance}px`,
                        '--kiki-size': `${p.size}px`,
                        animationDelay: `${p.delay}ms`
                      }}
                      aria-hidden="true"
                    />
                  ))}
                  <img
                    src="/kvaerstellung_button.png"
                    alt=""
                    className={`relative w-full h-full object-contain transition-all duration-200 ${
                      kvaEffectivelyAktiv ? '' : 'grayscale contrast-75'
                    }`}
                    draggable={false}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold tracking-wide uppercase text-text-muted">
                      KVA-Automatisierung
                    </span>
                    {isLevel3 && <Lock className="w-3 h-3 text-text-muted" aria-hidden="true" />}
                  </div>
                  <div className={`mt-0.5 text-base font-semibold ${kvaEffectivelyAktiv ? 'text-primary' : 'text-text'}`}>
                    {kvaEffectivelyAktiv ? 'Aktiv' : 'Inaktiv'}
                  </div>
                  <p className="mt-1 text-xs text-text-muted leading-snug">
                    {isLevel3
                      ? 'Im Kiki-Level „Nur Daten sammeln" nicht verfügbar — Kiki erstellt in diesem Modus generell keine Kostenvoranschläge.'
                      : 'Wenn aktiviert, kann Kiki Kostenvoranschläge im Gespräch erstellen.'}
                  </p>
                </div>
              </button>
            ) : (
              <KiFeatureCard
                src="/kiki-icons/kva.svg?v=2"
                active={kvaEffectivelyAktiv}
                disabled={readOnly || isLevel3}
                locked={isLevel3}
                bling={blingKva}
                label="KVA-Automatisierung"
                lockHint={'In der Autonomiestufe „Nur Daten sammeln" nicht verfügbar — wechsle auf Level 2 oder 3 zum Aktivieren.'}
                description={isLevel3
                  ? 'Im Modus „Nur Daten sammeln" nicht verfügbar — es werden generell keine Kostenvoranschläge erstellt.'
                  : 'Wenn aktiviert, können die Agenten Kostenvoranschläge erstellen.'}
                onClick={() => {
                  if (readOnly || isLevel3) return;
                  const newVal = !settings.kvaErstellungAktiv;
                  setSettings(prev => ({ ...prev, kvaErstellungAktiv: newVal }));
                  handleSaveSettings({ kvaErstellungAktiv: newVal });
                  if (newVal) {
                    setBlingKva(true);
                    setTimeout(() => setBlingKva(false), 1000);
                  }
                }}
              />
            )}

            {isHeykikiOrg && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs font-semibold text-blue-800 mb-2">Verhalten je nach Kiki-Level</p>
                <ul className="text-xs text-blue-700 space-y-1.5">
                  <li>
                    <span className="font-semibold">Kiki-Level 1 (Nur Daten sammeln):</span> Kiki erstellt keinen Kostenvoranschlag. Der Wunsch wird nur als Nachricht notiert; den KVA erstellen Sie anschließend selbst.
                  </li>
                  <li>
                    <span className="font-semibold">Kiki-Level 2 (Mit Bestätigung):</span> Kiki erstellt den Kostenvoranschlag im Gespräch. Sie erhalten eine Benachrichtigung und geben den Versand frei — der Kunde bekommt die E-Mail erst nach Ihrer Freigabe.
                  </li>
                  <li>
                    <span className="font-semibold">Kiki-Level 3 (Voll automatisch):</span> Kiki erstellt den Kostenvoranschlag im Gespräch und versendet ihn direkt per E-Mail an den Kunden.
                  </li>
                </ul>
              </div>
            )}

            {/* KVA-Zusatz-Toggles (kvaAutoSenden / kvaBenachrichtigung) aus der
                KI-Zentrale entfernt (2026-06) — Standalone-KVA ist jetzt ein
                reiner An/Aus-Schalter (Parität mit HeyKiki). Backend-Settings
                bleiben in der DB bestehen und werden weiter gelesen; sie sind
                nur nicht mehr über die KI-Zentrale steuerbar. Das Sende-/
                Benachrichtigungs-Verhalten kann später ans Autonomie-Level
                gekoppelt werden (wie bei HeyKiki). */}

            {/* Auftragsbestätigung-Automatik (AB) aus der KI-Zentrale entfernt
                (2026-06) — UI-Parität mit HeyKiki, das diese standalone-only
                Toggles nicht zeigt. Das Backend-Setting abAutoErstellen/
                abAutoVersenden bleibt in der DB bestehen (WhatsApp/E-Mail lesen
                es weiterhin); es wird nur nicht mehr über die KI-Zentrale
                gesteuert. Bei Bedarf reaktivieren = diesen Block zurückholen. */}
          </div>
        </Collapsible>
      </div>

      {/* === NOTDIENST (HEYKIKI-only) === */}
      {/* Lucas-Feedback 2026-05-09 (Husmann & Dreier): Pro-Org-Notdienst-
          Konfiguration mit Keywords, Aktivzeit-Steuerung und Aufpreis-Hinweis.
          Container liegt bewusst direkt unter „Kostenvoranschlag-Automatisierung",
          weil dort auch die Notdienst-Nummer hingewandert ist (vorher Telefon-
          Tab). Standalone-Orgs sehen den Block nicht — sie pflegen die
          Notdienst-Nummer weiterhin im Telefon-Agent-Tab. */}
      {isHeykikiOrg && (
        <NotdienstSection
          settings={settings}
          setSettings={setSettings}
          handleSaveSettings={handleSaveSettings}
          voiceSettings={voiceSettings}
          handleSaveEmergencyNumber={handleSaveEmergencyNumber}
          expanded={expandedSections.notdienst}
          SectionHeader={SectionHeader}
          readOnly={readOnly}
        />
      )}

      {/* === THEMENSPEZIFISCHE PFLICHTFELDER (Legacy) ===
          Wird durch den EINEN Leitfaden (Allgemeine Pflichtfelder) abgelöst.
          Nur noch sichtbar, solange eine Org noch aktive Themen pflegt — so
          verschwindet die Sektion für neue/saubere Standalone-Orgs (Leitfaden-
          Ansicht), während Orgs mit Bestand (z.B. Glaserei Scharf) ihre aktiven
          Themen weiter verwalten können und nichts still im Hintergrund hängt. */}
      {!isHeykikiOrg && themen.length > 0 && (
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="themen" title="Themenspezifische Pflichtfelder (Legacy)">
          {!isHeykikiOrg && (
            <span className="text-xs font-normal text-text-muted bg-primary/10 px-2 py-0.5 rounded-full">Alle Agenten</span>
          )}
          {!expandedSections.themen && (
            <span className="text-sm font-normal text-text-muted">
              · {themen.length > 0 ? `${themen.length} ${themen.length === 1 ? 'Thema' : 'Themen'}` : 'Keine Themen'}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.themen}>
          <div className="mt-2">
            {/* Erklärung als visueller Flow */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-5">
              <p className="text-sm font-semibold text-blue-900 mb-3">So funktioniert die themenbasierte Abfrage:</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <div className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm">
                  <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-blue-800">Kunde nennt Anliegen</span>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm">
                  <Search className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-blue-800">{isHeykikiOrg ? 'Kiki erkennt Thema per Keywords' : 'KI erkennt Thema per Keywords'}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm">
                  <Tag className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-blue-800">Themen-Felder werden abgefragt</span>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm">
                  <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-blue-800">Alle Felder vollständig</span>
                </div>
                <ArrowRight className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border border-blue-200 shadow-sm">
                  <Calendar className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-blue-800">Termin wird gesucht</span>
                </div>
              </div>
              <p className="text-xs text-blue-700 mt-3">
                Kein Thema erkannt? Dann gelten nur die allgemeinen Pflichtfelder oben.
              </p>
            </div>

            {/* Themen-Liste */}
            {themen.length === 0 ? (
              <div className="text-center py-6 text-sm text-text-muted border-2 border-dashed border-border rounded-lg">
                Noch keine Themen konfiguriert. Erstellen Sie ein Thema, um themenspezifische Pflichtfelder zu aktivieren.
              </div>
            ) : (
              <div className="space-y-3">
                {themen.map(thema => (
                  <ThemaCard
                    key={thema.id}
                    thema={thema}
                    expanded={expandedThemen[thema.id] || false}
                    onToggle={() => setExpandedThemen(prev => ({ ...prev, [thema.id]: !prev[thema.id] }))}
                    readOnly={readOnly}
                    isHeykikiOrg={isHeykikiOrg}
                    editing={editingThema?.id === thema.id ? editingThema : null}
                    onStartEdit={() => setEditingThema({
                      id: thema.id,
                      name: thema.name,
                      beschreibung: thema.beschreibung || '',
                      keywords: (thema.keywords || []).join(', '),
                    })}
                    onCancelEdit={() => setEditingThema(null)}
                    onSaveEdit={() => handleUpdateThema(thema.id)}
                    onEditChange={(field, value) => setEditingThema(prev => ({ ...prev, [field]: value }))}
                    deleteConfirm={deleteConfirm === thema.id}
                    onDeleteClick={() => setDeleteConfirm(thema.id)}
                    onDeleteCancel={() => setDeleteConfirm(null)}
                    onDeleteConfirm={() => handleDeleteThema(thema.id)}
                    onSaveFelder={(felder) => handleSaveThemenFelder(thema.id, felder)}
                  />
                ))}
              </div>
            )}

            {/* Neues Thema hinzufügen */}
            {!readOnly && (
              <div className="mt-4">
                {showNewThemaForm ? (
                  <div className="border border-primary/30 rounded-lg p-4 bg-primary/5">
                    <h4 className="text-sm font-semibold mb-3">Neues Thema erstellen</h4>
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Thema-Name (z.B. Dach, Kamin, Fenster) *"
                        value={newThema.name}
                        onChange={(e) => setNewThema(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                        autoFocus
                      />
                      <input
                        type="text"
                        placeholder="Beschreibung (optional)"
                        value={newThema.beschreibung}
                        onChange={(e) => setNewThema(prev => ({ ...prev, beschreibung: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                      />
                      <div>
                        <input
                          type="text"
                          placeholder="Keywords (kommasepariert, z.B. Dachziegel, Dachrinne, Dachschaden)"
                          value={newThema.keywords}
                          onChange={(e) => setNewThema(prev => ({ ...prev, keywords: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                        />
                        <p className="text-xs text-text-muted mt-1">{isHeykikiOrg ? 'Kiki nutzt diese Keywords, um das Thema im Gespräch zu erkennen.' : 'Die KI nutzt diese Keywords, um das Thema im Gespräch zu erkennen.'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleCreateThema}
                          disabled={!newThema.name.trim()}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm disabled:opacity-50"
                        >
                          Thema erstellen
                        </button>
                        <button
                          onClick={() => { setShowNewThemaForm(false); setNewThema({ name: '', beschreibung: '', keywords: '' }); }}
                          className="px-4 py-2 text-sm text-text-muted hover:text-text"
                        >
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNewThemaForm(true)}
                    className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-border rounded-lg text-sm text-text-muted hover:border-primary hover:text-primary transition w-full justify-center"
                  >
                    <Plus className="w-4 h-4" />
                    Neues Thema hinzufügen
                  </button>
                )}
              </div>
            )}
          </div>
        </Collapsible>
      </div>
      )}

      {/* Preisauskunft */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="preisauskunft" title="Preisauskunft">
          {!expandedSections.preisauskunft && (
            <span className="text-sm font-normal text-text-muted">
              · {settings.priceInfoEnabled ? 'Aktiv' : 'Inaktiv'}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.preisauskunft}>
          <div className="mt-2">
            {isHeykikiOrg ? (
              <button
                type="button"
                disabled={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  const newVal = !settings.priceInfoEnabled;
                  setSettings(prev => ({ ...prev, priceInfoEnabled: newVal }));
                  handleSaveSettings({ priceInfoEnabled: newVal });
                  if (newVal) {
                    setBlingPreis(true);
                    setTimeout(() => setBlingPreis(false), 1000);
                  }
                }}
                className={`kiki-level-btn relative w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
                  settings.priceInfoEnabled
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-white hover:border-primary/40 opacity-80 hover:opacity-100'
                } ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'} ${blingPreis ? 'kiki-level-bling' : ''}`}
                aria-pressed={settings.priceInfoEnabled}
                aria-label={`Preisauskunft ${settings.priceInfoEnabled ? 'aktiv' : 'inaktiv'}`}
              >
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center flex-shrink-0">
                  {/* Bling-Ringe pulsen vom Avatar aus (statt vom ganzen Button),
                      damit sie auch bei breiten Pillen-Buttons innerhalb des
                      Avatar-Bereichs bleiben und nicht vom Page-Layout
                      (overflow-x: hidden) gekappt werden. */}
                  <span className="kiki-level-bling-ring pointer-events-none" aria-hidden="true" />
                  <span className="kiki-level-bling-ring kiki-level-bling-ring-delayed pointer-events-none" aria-hidden="true" />
                  <span
                    aria-hidden="true"
                    className={`absolute inset-[-20%] rounded-full pointer-events-none transition-opacity duration-300 ${
                      settings.priceInfoEnabled ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{
                      background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 55%, transparent) 0%, color-mix(in srgb, var(--primary) 25%, transparent) 40%, transparent 70%)',
                      filter: 'blur(14px)',
                    }}
                  />
                  <span className="kiki-level-flash pointer-events-none" aria-hidden="true" />
                  {KIKI_PARTICLES.map((p, i) => (
                    <span
                      key={i}
                      className={`kiki-particle kiki-particle-${p.kind} pointer-events-none`}
                      style={{
                        '--kiki-a': `${p.angle}deg`,
                        '--kiki-d': `${p.distance}px`,
                        '--kiki-size': `${p.size}px`,
                        animationDelay: `${p.delay}ms`
                      }}
                      aria-hidden="true"
                    />
                  ))}
                  <img
                    src="/preisnennung.png"
                    alt=""
                    className={`relative w-full h-full object-contain transition-all duration-200 ${
                      settings.priceInfoEnabled ? '' : 'grayscale contrast-75'
                    }`}
                    draggable={false}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold tracking-wide uppercase text-text-muted">
                    Preisauskunft
                  </span>
                  <div className={`mt-0.5 text-base font-semibold ${settings.priceInfoEnabled ? 'text-primary' : 'text-text'}`}>
                    {settings.priceInfoEnabled ? 'Aktiv' : 'Inaktiv'}
                  </div>
                  <p className="mt-1 text-xs text-text-muted leading-snug">
                    {isHeykikiOrg ? 'Wenn aktiviert, kann Kiki ungefähre Preise aus Ihrem Katalog nennen.' : 'Wenn aktiviert, können die KI-Agenten ungefähre Preise aus Ihrem Katalog nennen.'}
                  </p>
                </div>
              </button>
            ) : (
              <KiFeatureCard
                src="/kiki-icons/preis.svg?v=2"
                active={!!settings.priceInfoEnabled}
                disabled={readOnly}
                bling={blingPreis}
                label="Preisauskunft"
                description="Wenn aktiviert, können die Agenten ungefähre Preise aus Ihrem Katalog nennen."
                onClick={() => {
                  if (readOnly) return;
                  const newVal = !settings.priceInfoEnabled;
                  setSettings(prev => ({ ...prev, priceInfoEnabled: newVal }));
                  handleSaveSettings({ priceInfoEnabled: newVal });
                  if (newVal) {
                    setBlingPreis(true);
                    setTimeout(() => setBlingPreis(false), 1000);
                  }
                }}
              />
            )}
          </div>
        </Collapsible>
      </div>

      {/* Branche — nur bei Standalone-Orgs an dieser Stelle. */}
      {!isHeykikiOrg && brancheBlock}

      {/* Leistungen */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="leistungen" title="Angebotene Leistungen">
          {!expandedSections.leistungen && (() => {
            const ang = (leistungen?.angebotenLeistungen || []).length;
            const nicht = (leistungen?.nichtAngeboten || []).length;
            if (ang + nicht === 0) return null;
            return (
              <span className="text-sm font-normal text-text-muted">
                · {ang} angeboten{nicht > 0 ? `, ${nicht} nicht angeboten` : ''}
              </span>
            );
          })()}
        </SectionHeader>
        <Collapsible isOpen={!!(expandedSections.leistungen && leistungen)}>
          <div className="mt-2 space-y-4">
            {/* Angebotene Leistungen */}
            <div>
              <label className="text-sm font-medium text-text mb-2 block">Angebotene Leistungen</label>
              <div className="flex flex-wrap gap-2">
                {(leistungen.angebotenLeistungen || []).map((l, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm border border-green-200">
                    {l}
                    {!readOnly && (
                      <button onClick={() => {
                        const nextAng = (leistungen.angebotenLeistungen || []).filter((_, idx) => idx !== i);
                        setLeistungen(prev => ({ ...prev, angebotenLeistungen: nextAng }));
                        handleSaveLeistungen({ angebotenLeistungen: nextAng, nichtAngeboten: leistungen.nichtAngeboten });
                      }}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!readOnly && (
                <AddItemInput
                  placeholder="Leistung hinzufügen..."
                  onAdd={(val) => {
                    const nextAng = [...(leistungen.angebotenLeistungen || []), val];
                    setLeistungen(prev => ({ ...prev, angebotenLeistungen: nextAng }));
                    handleSaveLeistungen({ angebotenLeistungen: nextAng, nichtAngeboten: leistungen.nichtAngeboten });
                  }}
                />
              )}
            </div>

            {/* Nicht angebotene Leistungen */}
            <div>
              <label className="text-sm font-medium text-text mb-2 block">Nicht angebotene Leistungen</label>
              <div className="flex flex-wrap gap-2">
                {(leistungen.nichtAngeboten || []).map((l, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm border border-red-200">
                    {l}
                    {!readOnly && (
                      <button onClick={() => {
                        const nextNicht = (leistungen.nichtAngeboten || []).filter((_, idx) => idx !== i);
                        setLeistungen(prev => ({ ...prev, nichtAngeboten: nextNicht }));
                        handleSaveLeistungen({ angebotenLeistungen: leistungen.angebotenLeistungen, nichtAngeboten: nextNicht });
                      }}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!readOnly && (
                <AddItemInput
                  placeholder="Nicht angebotene Leistung..."
                  onAdd={(val) => {
                    const nextNicht = [...(leistungen.nichtAngeboten || []), val];
                    setLeistungen(prev => ({ ...prev, nichtAngeboten: nextNicht }));
                    handleSaveLeistungen({ angebotenLeistungen: leistungen.angebotenLeistungen, nichtAngeboten: nextNicht });
                  }}
                />
              )}
            </div>
          </div>
        </Collapsible>
      </div>

      {/* Geschäftszeiten — für HeyKiki-Orgs direkt in der KiKi-Zentrale statt im Kalender. */}
      {isHeykikiOrg && (
        <div className="bg-surface rounded-card shadow-card p-5">
          <SectionHeader id="geschaeftszeiten" title="Geschäftszeiten" />
          <Collapsible isOpen={expandedSections.geschaeftszeiten}>
            <div className="mt-2">
              <Geschaeftszeiten embedded />
            </div>
          </Collapsible>
        </div>
      )}

      {/* Urlaubsmodus */}
      {!isHeykikiOrg && (
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="urlaubsmodus" title="Betriebsurlaub">
          <Plane className="w-4 h-4 text-text-muted" />
          {!expandedSections.urlaubsmodus && (
            <span className="text-sm font-normal text-text-muted">
              · {settings.urlaubAktiv ? 'Aktiv' : 'Inaktiv'}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.urlaubsmodus}>
          <div className="mt-2 space-y-4">
            <p className="text-sm text-text-muted">
              Hinterlegen Sie Ihren Betriebsurlaub. Die KI informiert Kunden bei der ersten Nachricht über den Urlaub, beantwortet aber weiterhin alle Fragen die sie kann.
            </p>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <span className="text-sm font-medium">Betriebsurlaub aktivieren</span>
                <p className="text-xs text-text-muted mt-0.5">
                  Die KI weist Kunden auf den Betriebsurlaub hin und verweist bei offenen Themen auf das Rückkehrdatum.
                </p>
              </div>
              <div
                onClick={!readOnly ? () => {
                  const newVal = !settings.urlaubAktiv;
                  setSettings(prev => ({ ...prev, urlaubAktiv: newVal }));
                  handleSaveSettings({ urlaubAktiv: newVal });
                } : undefined}
                className={`relative w-11 h-6 rounded-full transition cursor-pointer flex-shrink-0 ${settings.urlaubAktiv ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.urlaubAktiv ? 'translate-x-5' : ''}`} />
              </div>
            </div>
            {settings.urlaubAktiv && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg border border-border">
                    <label className="text-sm font-medium text-text block mb-1">Von</label>
                    <p className="text-xs text-text-muted mb-2">Beginn des Betriebsurlaubs (Datum und Uhrzeit).</p>
                    <input
                      type="datetime-local"
                      value={settings.urlaubVon ? new Date(new Date(settings.urlaubVon).getTime() - new Date(settings.urlaubVon).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                      onChange={(e) => {
                        const val = e.target.value ? new Date(e.target.value).toISOString() : null;
                        setSettings(prev => ({ ...prev, urlaubVon: val }));
                      }}
                      onBlur={() => handleSaveSettings({ urlaubVon: settings.urlaubVon })}
                      disabled={readOnly}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </div>
                  <div className="p-4 rounded-lg border border-border">
                    <label className="text-sm font-medium text-text block mb-1">Bis</label>
                    <p className="text-xs text-text-muted mb-2">Ende des Betriebsurlaubs (Datum und Uhrzeit).</p>
                    <input
                      type="datetime-local"
                      value={settings.urlaubBis ? new Date(new Date(settings.urlaubBis).getTime() - new Date(settings.urlaubBis).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                      onChange={(e) => {
                        const val = e.target.value ? new Date(e.target.value).toISOString() : null;
                        setSettings(prev => ({ ...prev, urlaubBis: val }));
                      }}
                      onBlur={() => handleSaveSettings({ urlaubBis: settings.urlaubBis })}
                      disabled={readOnly}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </div>
                </div>
                {settings.urlaubVon && settings.urlaubBis && new Date(settings.urlaubVon) >= new Date(settings.urlaubBis) && (
                  <p className="text-sm text-red-500">Das Startdatum muss vor dem Enddatum liegen.</p>
                )}
                {settings.urlaubVon && settings.urlaubBis && new Date(settings.urlaubBis) < new Date() && new Date(settings.urlaubVon) < new Date(settings.urlaubBis) && (
                  <p className="text-sm text-amber-500">Der Urlaubszeitraum liegt in der Vergangenheit. Die KI wird keinen Urlaubshinweis anzeigen.</p>
                )}
                <div className="p-4 rounded-lg border border-border">
                  <label className="text-sm font-medium text-text block mb-1">Urlaubsnachricht</label>
                  <p className="text-xs text-text-muted mb-2">Individuelle Nachricht, die Kunden während des Betriebsurlaubs mitgeteilt wird. Wenn leer, wird eine Standardnachricht verwendet.</p>
                  <textarea
                    value={settings.urlaubNachricht || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, urlaubNachricht: e.target.value }))}
                    onBlur={() => handleSaveSettings({ urlaubNachricht: settings.urlaubNachricht || '' })}
                    disabled={readOnly}
                    rows={3}
                    placeholder="z.B. Wir befinden uns vom 01.07. bis 15.07. im Betriebsurlaub. Ab dem 16.07. sind wir wieder für Sie da."
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y"
                  />
                </div>
              </div>
            )}
          </div>
        </Collapsible>
      </div>
      )}

      {/* E-Mail-Benachrichtigungen */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="emailBenachrichtigungen" title="E-Mail-Benachrichtigungen">
          <Mail className="w-4 h-4 text-text-muted" />
          {!expandedSections.emailBenachrichtigungen && (
            <span className="text-sm font-normal text-text-muted">
              · {settings.emailBenachrichtigungenAktiv ? 'Aktiv' : 'Inaktiv'}
            </span>
          )}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.emailBenachrichtigungen}>
          <div className="mt-2 space-y-3">
            <p className="text-sm text-text-muted">
              {isHeykikiOrg
                ? 'Erhalten Sie eine E-Mail, wenn Kiki eine Benachrichtigung erstellt.'
                : 'Erhalten Sie eine E-Mail, wenn einer der KI-Agenten (Telefon, WhatsApp, E-Mail, Chat) eine Benachrichtigung erstellt.'}
            </p>
            <div className="flex items-center justify-between p-3 rounded-lg border border-border">
              <div>
                <span className="text-sm font-medium">E-Mail-Weiterleitung aktiv</span>
                <p className="text-xs text-text-muted mt-0.5">
                  {isHeykikiOrg
                    ? 'Kiki-Benachrichtigungen werden zusätzlich per E-Mail an die angegebenen Adressen gesendet'
                    : 'KI-Benachrichtigungen werden zusätzlich per E-Mail an die angegebenen Adressen gesendet'}
                </p>
              </div>
              <div
                onClick={!readOnly ? () => {
                  const newVal = !settings.emailBenachrichtigungenAktiv;
                  setSettings(prev => ({ ...prev, emailBenachrichtigungenAktiv: newVal }));
                  handleSaveSettings({ emailBenachrichtigungenAktiv: newVal });
                } : undefined}
                className={`relative w-11 h-6 rounded-full transition cursor-pointer flex-shrink-0 ${settings.emailBenachrichtigungenAktiv ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.emailBenachrichtigungenAktiv ? 'translate-x-5' : ''}`} />
              </div>
            </div>
            {settings.emailBenachrichtigungenAktiv && (
              <EmailTagInput
                emails={(settings.emailBenachrichtigungenAdresse || '').split(',').map(e => e.trim()).filter(Boolean)}
                onChange={(emails) => {
                  const joined = emails.join(', ') || null;
                  setSettings(prev => ({ ...prev, emailBenachrichtigungenAdresse: joined }));
                  handleSaveSettings({ emailBenachrichtigungenAdresse: joined });
                }}
                disabled={readOnly}
                isHeykikiOrg={isHeykikiOrg}
              />
            )}
          </div>
        </Collapsible>
      </div>

      {/* Wissensbasis */}
      <div className="bg-surface rounded-card shadow-card p-5">
        <SectionHeader id="wissensbasis" title="Wissensbasis">
          <BookOpen className="w-4 h-4 text-text-muted" />
          {!expandedSections.wissensbasis && (() => {
            const len = (settings.knowledgeBase || '').trim().length;
            const parts = [];
            if (len > 0) parts.push(`${len} Zeichen`);
            if (wissensQuellenReadyCount > 0) {
              parts.push(`${wissensQuellenReadyCount} ${wissensQuellenReadyCount === 1 ? 'Quelle' : 'Quellen'}`);
            }
            return (
              <span className="text-sm font-normal text-text-muted">
                · {parts.length > 0 ? parts.join(', ') : 'Leer'}
              </span>
            );
          })()}
        </SectionHeader>
        <Collapsible isOpen={expandedSections.wissensbasis}>
          <div className="mt-2 space-y-4">
            <p className="text-sm text-text-muted">
              {isHeykikiOrg
                ? 'Zusätzliches Wissen, das Kiki nutzen soll (z.B. FAQ, spezielle Hinweise).'
                : 'Zusätzliches Wissen, das alle KI-Agenten nutzen sollen (z.B. FAQ, spezielle Hinweise).'}
            </p>
            {(() => {
              const kbValue = settings.knowledgeBase || '';
              const kbLen = kbValue.length;
              const overMax = kbLen > KNOWLEDGE_BASE_MAX_LIMIT;
              const overHard = kbLen > KNOWLEDGE_BASE_HARD_LIMIT;
              const overSoft = kbLen > KNOWLEDGE_BASE_SOFT_LIMIT;
              const counterColor = overMax ? 'text-red-600 font-medium'
                : overHard ? 'text-orange-600 font-medium'
                : overSoft ? 'text-yellow-700'
                : 'text-text-muted';
              return (
                <>
                  <textarea
                    value={kbValue}
                    onChange={(e) => setSettings(prev => ({ ...prev, knowledgeBase: e.target.value }))}
                    onBlur={() => {
                      // Hard-Stop bei > MAX: nicht speichern, User-Feedback geben.
                      // Backend lehnt ebenfalls ab (Schutz für API-direkte Aufrufe).
                      if ((settings.knowledgeBase || '').length > KNOWLEDGE_BASE_MAX_LIMIT) {
                        toast.error(`Wissensbasis zu lang (${(settings.knowledgeBase || '').length} Zeichen, max. ${KNOWLEDGE_BASE_MAX_LIMIT}). Bitte kürzen oder als PDF/URL in den Wissensquellen hinterlegen.`);
                        return;
                      }
                      handleSaveSettings({ knowledgeBase: settings.knowledgeBase });
                    }}
                    disabled={readOnly}
                    rows={6}
                    placeholder="z.B. Wir bieten auch Notdienst am Wochenende an..."
                    className={`w-full px-3 py-2 border rounded-lg text-sm resize-y ${overMax ? 'border-red-400' : overHard ? 'border-orange-400' : 'border-border'}`}
                  />
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-text-muted">
                      Tipp: Kompakte Verhaltensregeln und Kontext gehören hier rein. Längere Dokumente
                      (Preislisten, Service-Handbücher) lieber als PDF oder URL in den Wissensquellen unten
                      — die werden gezielt abgerufen, statt bei jedem Anruf mitzuziehen.
                    </span>
                    <span className={`whitespace-nowrap ml-3 ${counterColor}`}>
                      {kbLen.toLocaleString('de-DE')} / {KNOWLEDGE_BASE_MAX_LIMIT.toLocaleString('de-DE')} Zeichen
                    </span>
                  </div>
                  {overSoft && !overHard && (
                    <div className="flex gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-900">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        Der Text wird bei jedem Anruf-Turn in den System-Prompt geladen. Ab dieser Länge lohnt es
                        sich zu prüfen, ob Teile besser als PDF oder URL in den Wissensquellen hinterlegt werden —
                        dort werden sie nur bei passender Frage abgerufen.
                      </div>
                    </div>
                  )}
                  {overHard && !overMax && (
                    <div className="flex gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-900">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        Der Freitext nimmt jetzt deutlich Platz im Prompt ein. Das kann zentrale Anweisungen
                        verwässern. Bitte Inhalte, die nicht IMMER gelten müssen, in die Wissensquellen unten
                        verschieben — Limit ist {KNOWLEDGE_BASE_MAX_LIMIT.toLocaleString('de-DE')} Zeichen.
                      </div>
                    </div>
                  )}
                  {overMax && (
                    <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-900">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        Maximum überschritten. Speichern ist erst wieder möglich, wenn der Text auf max.
                        {' '}{KNOWLEDGE_BASE_MAX_LIMIT.toLocaleString('de-DE')} Zeichen gekürzt ist. Längere Inhalte
                        bitte als PDF oder URL in den Wissensquellen unten hinzufügen.
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Wissensquellen (PDFs & URLs) */}
            <WissensquellenSection
              readOnly={readOnly}
              onReadyCountChange={setWissensQuellenReadyCount}
            />
          </div>
        </Collapsible>
      </div>
    </div>
  );
}

// === WissensquellenSection Component ===
function WissensquellenSection({ readOnly, onReadyCountChange }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const [quellen, setQuellen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUrlForm, setShowUrlForm] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [urlTitel, setUrlTitel] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const pollingRef = useRef({});

  const loadQuellen = useCallback(async () => {
    try {
      const data = await kiZentraleApi.getWissensquellen();
      setQuellen(data);
      // Parent-Vorschau aktualisieren — nur READY-Quellen zählen, weil nur die
      // tatsächlich vom Agenten genutzt werden (siehe agentConfigService:
      // hasStructuredKnowledge zählt ebenfalls nur status='READY').
      if (onReadyCountChange) {
        onReadyCountChange(data.filter(q => q.status === 'READY').length);
      }

      // Polling für PENDING/PROCESSING Quellen starten
      data.forEach(q => {
        if ((q.status === 'PENDING' || q.status === 'PROCESSING') && !pollingRef.current[q.id]) {
          pollingRef.current[q.id] = setInterval(async () => {
            try {
              const status = await kiZentraleApi.getWissensquelleStatus(q.id);
              if (status.status === 'READY' || status.status === 'ERROR') {
                clearInterval(pollingRef.current[q.id]);
                delete pollingRef.current[q.id];
                loadQuellen();
              }
            } catch { /* ignore */ }
          }, 3000);
        }
      });
    } catch (err) {
      console.error('Wissensquellen laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, [onReadyCountChange]);

  useEffect(() => {
    loadQuellen();
    return () => {
      Object.values(pollingRef.current).forEach(clearInterval);
    };
  }, [loadQuellen]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Nur PDF-Dateien erlaubt');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 20 MB)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('titel', file.name.replace(/\.pdf$/i, ''));
      await kiZentraleApi.uploadWissensquelle(formData);
      toast.success('PDF wird verarbeitet...');
      await loadQuellen();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddUrl = async () => {
    const raw = urlInput.trim();
    const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;
    if (!/^https?:\/\/[^\s./]+\.[^\s]+/i.test(normalized)) {
      toast.error('Bitte gültige URL eingeben (z.B. www.muster.de)');
      return;
    }
    setUploading(true);
    try {
      await kiZentraleApi.addWissensquelleUrl({ url: normalized, titel: urlTitel || undefined });
      toast.success('URL wird verarbeitet...');
      setUrlInput('');
      setUrlTitel('');
      setShowUrlForm(false);
      await loadQuellen();
    } catch (err) {
      toast.error(err.response?.data?.error || 'URL hinzufügen fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, titel) => {
    if (!confirm(`"${titel}" wirklich löschen?`)) return;
    try {
      await kiZentraleApi.deleteWissensquelle(id);
      setQuellen(prev => prev.filter(q => q.id !== id));
      toast.success('Quelle gelöscht');
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleReprocess = async (id) => {
    try {
      await kiZentraleApi.reprocessWissensquelle(id);
      toast.success('Wird neu verarbeitet...');
      await loadQuellen();
    } catch {
      toast.error('Neu-Verarbeiten fehlgeschlagen');
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'READY':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />Bereit</span>;
      case 'PROCESSING':
      case 'PENDING':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><Loader2 className="w-3 h-3 animate-spin" />Verarbeitung...</span>;
      case 'ERROR':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />Fehler</span>;
      default:
        return null;
    }
  };

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-text flex items-center gap-2">
          <FileText className="w-4 h-4 text-text-muted" />
          Wissensquellen (PDFs & URLs)
        </h4>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUrlForm(!showUrlForm)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-gray-50 transition"
            >
              <Globe className="w-3.5 h-3.5" /> URL
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} PDF
            </button>
            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleFileUpload} />
          </div>
        )}
      </div>

      <p className="text-xs text-text-muted mb-3">
        {isHeykikiOrg
          ? 'PDFs und Webseiten werden automatisch analysiert. Kiki nutzt relevante Inhalte zur Beantwortung von Kundenanfragen.'
          : 'PDFs und Webseiten werden automatisch analysiert. Die KI-Agenten (WhatsApp, E-Mail, Telefon) nutzen relevante Inhalte zur Beantwortung von Kundenanfragen.'}
      </p>

      {/* URL-Eingabe */}
      {showUrlForm && (
        <div className="flex gap-2 mb-3">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://www.beispiel.de/faq"
            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
          />
          <input
            type="text"
            value={urlTitel}
            onChange={(e) => setUrlTitel(e.target.value)}
            placeholder="Titel (optional)"
            className="w-40 px-3 py-2 border border-border rounded-lg text-sm"
          />
          <button
            onClick={handleAddUrl}
            disabled={uploading || !urlInput}
            className="px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Hinzufügen'}
          </button>
          <button onClick={() => { setShowUrlForm(false); setUrlInput(''); setUrlTitel(''); }} className="px-2 py-2 text-text-muted hover:text-text">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Quellenliste */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
        </div>
      ) : quellen.length === 0 ? (
        <div className="text-center py-6 text-text-muted text-sm border border-dashed border-border rounded-lg">
          Noch keine Wissensquellen. Laden Sie PDFs hoch oder fügen Sie URLs hinzu.
        </div>
      ) : (
        <div className="space-y-2">
          {quellen.map(q => (
            <div key={q.id} className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-gray-50/50 transition">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                {q.typ === 'PDF' ? <FileText className="w-4 h-4 text-red-500" /> : <Globe className="w-4 h-4 text-blue-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-text truncate">{q.titel}</span>
                  {statusBadge(q.status)}
                </div>
                <div className="text-xs text-text-muted mt-0.5">
                  {q.typ === 'PDF' && q.dateiname && <span>{q.dateiname}</span>}
                  {q.typ === 'URL' && q.url && <span className="truncate block max-w-xs">{q.url}</span>}
                  {q.status === 'READY' && <span className="ml-2">{q.chunkAnzahl} Chunks</span>}
                  {q.status === 'ERROR' && q.fehlerMeldung && (
                    <span className="text-red-500 ml-2" title={q.fehlerMeldung}>{q.fehlerMeldung.substring(0, 60)}</span>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleReprocess(q.id)}
                    title="Neu verarbeiten"
                    className="p-1.5 rounded hover:bg-gray-100 text-text-muted hover:text-primary transition"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(q.id, q.titel)}
                    title="Löschen"
                    className="p-1.5 rounded hover:bg-red-50 text-text-muted hover:text-red-500 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === ThemaCard Component ===
function ThemaCard({
  thema, expanded, onToggle, readOnly, isHeykikiOrg,
  editing, onStartEdit, onCancelEdit, onSaveEdit, onEditChange,
  deleteConfirm, onDeleteClick, onDeleteCancel, onDeleteConfirm,
  onSaveFelder,
}) {
  const [newFeldKey, setNewFeldKey] = useState('');
  const [newFeldLabel, setNewFeldLabel] = useState('');

  const felder = thema.pflichtfelder || [];

  const handleAddFeld = () => {
    const label = newFeldLabel.trim();
    if (!label) return;
    // HeyKiki: Key aus Label ableiten (slugify / Standard-Alias).
    // Standalone: Key wird separat eingegeben (alte Logik).
    const key = isHeykikiOrg ? deriveFeldKeyFromLabel(label) : newFeldKey.trim();
    if (!key) return;
    // Duplikat-Schutz — selber feldKey im selben Thema ersetzt den Eintrag nicht
    // doppelt, sondern behält den bestehenden (weil der Server auf feldKey
    // dedupliziert).
    if (felder.some(f => f.feldKey === key)) {
      setNewFeldKey('');
      setNewFeldLabel('');
      return;
    }
    const updated = [
      ...felder,
      { feldKey: key, label, typ: 'text', pflicht: true },
    ];
    onSaveFelder(updated);
    setNewFeldKey('');
    setNewFeldLabel('');
  };

  const handleRemoveFeld = (feldKey) => {
    onSaveFelder(felder.filter(f => f.feldKey !== feldKey));
  };

  const handleTogglePflicht = (feldKey) => {
    onSaveFelder(felder.map(f =>
      f.feldKey === feldKey ? { ...f, pflicht: !f.pflicht } : f
    ));
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Thema Header */}
      <div
        className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition gap-2"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Tag className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-sm font-semibold truncate">{thema.name}</span>
          <span className="text-xs text-text-muted bg-white px-2 py-0.5 rounded border flex-shrink-0 whitespace-nowrap">
            {felder.length} {felder.length === 1 ? 'Feld' : 'Felder'}
          </span>
          {thema.keywords?.length > 0 && (
            <div className="hidden sm:flex items-center gap-1 flex-wrap">
              {thema.keywords.slice(0, 3).map((kw, i) => (
                <span key={i} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{kw}</span>
              ))}
              {thema.keywords.length > 3 && (
                <span className="text-xs text-text-muted">+{thema.keywords.length - 3}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
                className="p-1 text-text-muted hover:text-primary"
                title="Thema bearbeiten"
              >
                <Edit2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteClick(); }}
                className="p-1 text-text-muted hover:text-red-500"
                title="Thema löschen"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="p-3 bg-red-50 border-t border-red-200 flex items-center justify-between">
          <span className="text-sm text-red-700">Thema "{thema.name}" wirklich löschen?</span>
          <div className="flex items-center gap-2">
            <button onClick={onDeleteConfirm} className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700">Löschen</button>
            <button onClick={onDeleteCancel} className="px-3 py-1 text-sm text-text-muted hover:text-text">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Editing form */}
      {editing && (
        <div className="p-4 border-t border-border bg-yellow-50/50">
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Thema-Name *"
              value={editing.name}
              onChange={(e) => onEditChange('name', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
            <input
              type="text"
              placeholder="Beschreibung (optional)"
              value={editing.beschreibung}
              onChange={(e) => onEditChange('beschreibung', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
            <div>
              <input
                type="text"
                placeholder="Keywords (kommasepariert)"
                value={editing.keywords}
                onChange={(e) => onEditChange('keywords', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg"
              />
              {editing.keywords && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editing.keywords.split(',').map((kw, i) => kw.trim() && (
                    <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">{kw.trim()}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onSaveEdit} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">Speichern</button>
              <button onClick={onCancelEdit} className="px-4 py-2 text-sm text-text-muted hover:text-text">Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded content - Pflichtfelder */}
      {expanded && !editing && (
        <div className="p-4 border-t border-border">
          {thema.beschreibung && (
            <p className="text-xs text-text-muted mb-3">{thema.beschreibung}</p>
          )}

          {/* Keywords anzeigen */}
          {thema.keywords?.length > 0 && (
            <div className="mb-3">
              <span className="text-xs font-medium text-text-muted">Keywords: </span>
              <div className="inline-flex flex-wrap gap-1 ml-1">
                {thema.keywords.map((kw, i) => (
                  <span key={i} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">{kw}</span>
                ))}
              </div>
            </div>
          )}

          {/* Pflichtfelder Liste */}
          <div className="space-y-2">
            {felder.length === 0 ? (
              <p className="text-xs text-text-muted italic py-2">Noch keine Pflichtfelder für dieses Thema.</p>
            ) : (
              felder.map((feld) => (
                <div key={feld.feldKey} className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg border border-border bg-white">
                  <div className="flex items-center gap-2 min-w-0">
                    <GripVertical className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{feld.label}</span>
                    {!isHeykikiOrg && (
                      <span className="text-xs text-text-muted bg-gray-100 px-2 py-0.5 rounded flex-shrink-0">{feld.feldKey}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={feld.pflicht !== false}
                        onChange={() => handleTogglePflicht(feld.feldKey)}
                        disabled={readOnly}
                        className="accent-primary"
                      />
                      <span className="text-xs text-text-muted">Pflicht</span>
                    </label>
                    {!readOnly && (
                      <button
                        onClick={() => handleRemoveFeld(feld.feldKey)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Neues Feld hinzufügen */}
          {!readOnly && (isHeykikiOrg ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3">
              <input
                type="text"
                placeholder="Pflichtfeld (z.B. Dachtyp, Dachlänge)"
                value={newFeldLabel}
                onChange={(e) => setNewFeldLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddFeld(); }}
                className="w-full sm:flex-1 px-3 py-2 text-sm border border-border rounded-lg"
              />
              <button
                onClick={handleAddFeld}
                disabled={!newFeldLabel.trim()}
                className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 self-end sm:self-auto"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3">
              <input
                type="text"
                placeholder="Schlüssel (z.B. dachtyp)"
                value={newFeldKey}
                onChange={(e) => setNewFeldKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                className="w-full sm:flex-1 px-3 py-2 text-sm border border-border rounded-lg"
              />
              <input
                type="text"
                placeholder="Label (z.B. Dachtyp)"
                value={newFeldLabel}
                onChange={(e) => setNewFeldLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddFeld(); }}
                className="w-full sm:flex-1 px-3 py-2 text-sm border border-border rounded-lg"
              />
              <button
                onClick={handleAddFeld}
                disabled={!newFeldKey.trim() || !newFeldLabel.trim()}
                className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 self-end sm:self-auto"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange, disabled }) {
  return (
    <label className="flex items-center justify-between p-3 rounded-lg border border-border cursor-pointer">
      <div>
        <span className="text-sm font-medium">{label}</span>
        {description && <p className="text-xs text-text-muted mt-0.5">{description}</p>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="accent-primary w-5 h-5"
      />
    </label>
  );
}

// === NOTDIENST-CONTAINER (HEYKIKI-only) ===
// Komplette Notdienst-Konfiguration: Aktivierung, Notdienst-Nummer (lebt
// auf VoiceAssistantSettings), Aktivzeit-Steuerung (außerhalb Geschäftszeiten
// ODER freie Zeitfenster), Keyword-Editor mit Gewerk-Vorlagen, Aufpreis-
// Hinweis-Toggle + bearbeitbarer Default-Text. Werden bei jedem Save zum
// ElevenLabs-Agent gesynct (siehe agentConfigService.renderNotdienst).
const NOTDIENST_KEYWORD_TEMPLATES = {
  shk: {
    label: 'SHK / Sanitär-Heizung-Klima',
    keywords: ['Wasserrohrbruch', 'Rohrverstopfung', 'Warmwasserausfall', 'Heizungsausfall', 'Gasgeruch', 'Kein Warmwasser'],
  },
  elektro: {
    label: 'Elektro',
    keywords: ['Stromausfall', 'Funkenflug', 'Sicherung defekt', 'Brennender Geruch', 'Kein Strom'],
  },
  schluessel: {
    label: 'Schlüsseldienst',
    keywords: ['Ausgesperrt', 'Schloss kaputt', 'Einbruchschaden', 'Tür klemmt'],
  },
  dach: {
    label: 'Dachdecker / Garten / Sturmschäden',
    keywords: ['Sturmschaden', 'Abgedecktes Dach', 'Umgestürzter Baum', 'Wassereintritt durchs Dach'],
  },
};
const NOTDIENST_DEFAULT_AUFPREIS_TEXT = 'Bitte beachten Sie, dass im Notdienst zusätzliche Kosten anfallen können.';
const NOTDIENST_WOCHENTAGE = [
  { tag: 1, label: 'Montag', kurz: 'Mo' },
  { tag: 2, label: 'Dienstag', kurz: 'Di' },
  { tag: 3, label: 'Mittwoch', kurz: 'Mi' },
  { tag: 4, label: 'Donnerstag', kurz: 'Do' },
  { tag: 5, label: 'Freitag', kurz: 'Fr' },
  { tag: 6, label: 'Samstag', kurz: 'Sa' },
  { tag: 0, label: 'Sonntag', kurz: 'So' },
];

export function NotdienstSection({ settings, setSettings, handleSaveSettings, voiceSettings, handleSaveEmergencyNumber, expanded, SectionHeader, readOnly, iconSrc }) {
  const aktiv = !!settings.notdienstAktiv;
  const nurAusserhalb = settings.notdienstNurAusserhalbGeschaeftszeiten !== false;
  const zeitfenster = Array.isArray(settings.notdienstZeitfenster) ? settings.notdienstZeitfenster : [];
  const keywords = Array.isArray(settings.notdienstKeywords) ? settings.notdienstKeywords : [];
  const aufpreisAktiv = !!settings.notdienstAufpreisHinweisAktiv;
  const aufpreisText = settings.notdienstAufpreisHinweisText ?? '';
  const emergencyNumber = voiceSettings?.emergencyForwardingNumber || '';

  const [emergencyDraft, setEmergencyDraft] = useState(emergencyNumber);
  const [keywordInput, setKeywordInput] = useState('');
  const [aufpreisDraft, setAufpreisDraft] = useState(aufpreisText);
  const [blingNotdienst, setBlingNotdienst] = useState(false);

  // Standalone (Telefon-Tab) übergibt `iconSrc` → neutrales, in der Org-
  // Akzentfarbe einfärbbares SVG statt Kiki-Maskottchen, plus neutrale Texte
  // („die KI" statt „Kiki", da Standalone-Orgs nicht HeyKiki-gebrandet sind).
  const agentName = iconSrc ? 'die KI' : 'Kiki';
  const agentNameCap = iconSrc ? 'Die KI' : 'Kiki';

  // SectionHeader/expanded sind optional: im Einstellungen-Tab (HeyKiki) steuert
  // der Parent das Aufklappen über `expandedSections`; im Telefon-Tab
  // (Standalone) verwaltet die Section ihren eigenen Aufklapp-Zustand.
  const [internalExpanded, setInternalExpanded] = useState(true);
  const isOpen = typeof expanded === 'boolean' ? expanded : internalExpanded;
  const Header = SectionHeader || (({ id, title, children }) => (
    <div
      className="flex items-center justify-between cursor-pointer py-3"
      onClick={() => setInternalExpanded(v => !v)}
    >
      <h3 className="text-base font-semibold text-text flex items-center gap-2">
        {title}
        {children}
      </h3>
      {isOpen ? <ChevronUp className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
    </div>
  ));

  // Emergency-Draft synchronisieren wenn voiceSettings (re-)laden
  useEffect(() => { setEmergencyDraft(voiceSettings?.emergencyForwardingNumber || ''); }, [voiceSettings?.emergencyForwardingNumber]);
  useEffect(() => { setAufpreisDraft(settings.notdienstAufpreisHinweisText ?? ''); }, [settings.notdienstAufpreisHinweisText]);

  const persistField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    handleSaveSettings({ [field]: value });
  };

  const addKeyword = (raw) => {
    const cleaned = String(raw || '').trim();
    if (!cleaned) return;
    if (keywords.some(k => k.toLowerCase() === cleaned.toLowerCase())) return;
    persistField('notdienstKeywords', [...keywords, cleaned]);
  };
  const removeKeyword = (kw) => {
    persistField('notdienstKeywords', keywords.filter(k => k !== kw));
  };
  const applyTemplate = (templateKey) => {
    const tpl = NOTDIENST_KEYWORD_TEMPLATES[templateKey];
    if (!tpl) return;
    const merged = [...keywords];
    for (const k of tpl.keywords) {
      if (!merged.some(existing => existing.toLowerCase() === k.toLowerCase())) merged.push(k);
    }
    persistField('notdienstKeywords', merged);
  };

  const addZeitfenster = () => {
    const next = [...zeitfenster, { tag: 6, von: '00:00', bis: '23:59' }];
    persistField('notdienstZeitfenster', next);
  };
  const updateZeitfenster = (idx, patch) => {
    const next = zeitfenster.map((z, i) => i === idx ? { ...z, ...patch } : z);
    persistField('notdienstZeitfenster', next);
  };
  const removeZeitfenster = (idx) => {
    const next = zeitfenster.filter((_, i) => i !== idx);
    persistField('notdienstZeitfenster', next.length > 0 ? next : null);
  };

  return (
    <div className="bg-surface rounded-card shadow-card p-5">
      <Header id="notdienst" title="Notdienst">
        {!isOpen && (
          <span className="text-sm font-normal text-text-muted">
            · {aktiv ? 'Aktiv' : 'Inaktiv'}
          </span>
        )}
      </Header>
      <Collapsible isOpen={isOpen}>
        <div className="mt-2 space-y-4">
          <button
            type="button"
            disabled={readOnly}
            onClick={() => {
              if (readOnly) return;
              const newVal = !aktiv;
              persistField('notdienstAktiv', newVal);
              if (newVal) {
                setBlingNotdienst(true);
                setTimeout(() => setBlingNotdienst(false), 1000);
              }
            }}
            className={`kiki-level-btn relative w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-200 text-left ${
              aktiv
                ? 'border-primary bg-primary/5 shadow-md'
                : 'border-border bg-white hover:border-primary/40 opacity-80 hover:opacity-100'
            } ${readOnly ? 'cursor-not-allowed' : 'cursor-pointer'} ${blingNotdienst ? 'kiki-level-bling' : ''}`}
            aria-pressed={aktiv}
            aria-label={`Notdienst ${aktiv ? 'aktiv' : 'inaktiv'}`}
          >
            {iconSrc ? (
              <KiIconStage src={iconSrc} active={aktiv} />
            ) : (
            <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center flex-shrink-0">
              <span className="kiki-level-bling-ring pointer-events-none" aria-hidden="true" />
              <span className="kiki-level-bling-ring kiki-level-bling-ring-delayed pointer-events-none" aria-hidden="true" />
              <span
                aria-hidden="true"
                className={`absolute inset-[-20%] rounded-full pointer-events-none transition-opacity duration-300 ${
                  aktiv ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  background: 'radial-gradient(circle, color-mix(in srgb, var(--primary) 55%, transparent) 0%, color-mix(in srgb, var(--primary) 25%, transparent) 40%, transparent 70%)',
                  filter: 'blur(14px)',
                }}
              />
              <span className="kiki-level-flash pointer-events-none" aria-hidden="true" />
              {KIKI_PARTICLES.map((p, i) => (
                <span
                  key={i}
                  className={`kiki-particle kiki-particle-${p.kind} pointer-events-none`}
                  style={{
                    '--kiki-a': `${p.angle}deg`,
                    '--kiki-d': `${p.distance}px`,
                    '--kiki-size': `${p.size}px`,
                    animationDelay: `${p.delay}ms`
                  }}
                  aria-hidden="true"
                />
              ))}
              <img
                src="/notdienst_button.png?v=2"
                alt=""
                className={`relative w-full h-full object-contain transition-all duration-200 ${
                  aktiv ? '' : 'grayscale contrast-75'
                }`}
                draggable={false}
              />
            </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-semibold tracking-wide uppercase text-text-muted">
                  Notdienst
                </span>
              </div>
              <div className={`mt-0.5 text-base font-semibold ${aktiv ? 'text-primary' : 'text-text'}`}>
                {aktiv ? 'Aktiv' : 'Inaktiv'}
              </div>
              <p className="mt-1 text-xs text-text-muted leading-snug">
                Wenn aktiviert, erkennt {agentName} definierte Notfall-Stichwörter im Anruf und kann sofort an die Notdienst-Nummer weiterleiten.
              </p>
            </div>
          </button>

          {aktiv && (
            <>
              {/* Notdienst-Nummer (lebt auf VoiceAssistantSettings) */}
              <div>
                <label className="text-sm font-medium text-text block mb-1">Notdienst-Nummer</label>
                <input
                  type="tel"
                  value={emergencyDraft}
                  onChange={(e) => setEmergencyDraft(e.target.value)}
                  onBlur={() => {
                    if (emergencyDraft !== (voiceSettings?.emergencyForwardingNumber || '')) {
                      handleSaveEmergencyNumber(emergencyDraft);
                    }
                  }}
                  disabled={readOnly}
                  placeholder="+49..."
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
                <p className="text-xs text-text-muted mt-1">
                  Bei akuten Notfällen während der Notdienst-Aktivzeit bietet {agentName} sofortige Weiterleitung an diese Nummer an. Wenn leer gelassen, wird das Anliegen mit hoher Dringlichkeit aufgenommen, ohne weiterzuleiten.
                </p>
              </div>

              {/* Aktivzeit-Steuerung */}
              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="text-sm font-semibold">Wann ist der Notdienst aktiv?</h4>
                <ToggleRow
                  label="Nur außerhalb der Geschäftszeiten"
                  description="Innerhalb der konfigurierten Geschäftszeiten gilt der reguläre Modus. Außerhalb wird der Notdienst-Modus automatisch aktiviert."
                  checked={nurAusserhalb}
                  onChange={(val) => persistField('notdienstNurAusserhalbGeschaeftszeiten', val)}
                  disabled={readOnly}
                />

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {nurAusserhalb ? 'Zusätzliche Zeitfenster (optional)' : 'Aktive Zeitfenster'}
                    </span>
                    <button
                      type="button"
                      onClick={addZeitfenster}
                      disabled={readOnly}
                      className="text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-md hover:bg-primary/20 disabled:opacity-50 inline-flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Zeitfenster
                    </button>
                  </div>
                  {!nurAusserhalb && zeitfenster.length === 0 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                      Ohne Zeitfenster und ohne den „Außerhalb Geschäftszeiten"-Toggle ist der Notdienst durchgehend (24/7) aktiv.
                    </p>
                  )}
                  {zeitfenster.length === 0 && nurAusserhalb && (
                    <p className="text-xs text-text-muted">
                      Standardmäßig gilt der Notdienst außerhalb der Geschäftszeiten. Optional kannst du zusätzliche Fenster definieren (z.B. Mittwochnachmittag).
                    </p>
                  )}
                  {zeitfenster.length > 0 && (
                    <div className="space-y-2">
                      {zeitfenster.map((z, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border border-border bg-surface-alt">
                          <select
                            value={z.tag}
                            onChange={(e) => updateZeitfenster(idx, { tag: parseInt(e.target.value, 10) })}
                            disabled={readOnly}
                            className="px-2 py-1.5 border border-border rounded-md text-sm"
                          >
                            {NOTDIENST_WOCHENTAGE.map(d => (
                              <option key={d.tag} value={d.tag}>{d.label}</option>
                            ))}
                          </select>
                          <input
                            type="time"
                            value={z.von || ''}
                            onChange={(e) => updateZeitfenster(idx, { von: e.target.value })}
                            disabled={readOnly}
                            className="px-2 py-1.5 border border-border rounded-md text-sm"
                          />
                          <span className="text-text-muted text-sm">–</span>
                          <input
                            type="time"
                            value={z.bis || ''}
                            onChange={(e) => updateZeitfenster(idx, { bis: e.target.value })}
                            disabled={readOnly}
                            className="px-2 py-1.5 border border-border rounded-md text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => removeZeitfenster(idx)}
                            disabled={readOnly}
                            className="ml-auto text-text-muted hover:text-red-500"
                            aria-label="Zeitfenster entfernen"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Keywords */}
              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="text-sm font-semibold">Notfall-Stichwörter</h4>
                <p className="text-xs text-text-muted">
                  Wenn der Anrufer eines dieser Stichwörter nennt UND aktuell Notdienst-Modus gilt, behandelt {agentName} das Anliegen als Notfall.
                </p>

                {keywords.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {keywords.map(kw => (
                      <span key={kw} className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-sm">
                        {kw}
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => removeKeyword(kw)}
                            className="hover:text-red-500"
                            aria-label={`${kw} entfernen`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-text-muted italic">Noch keine Stichwörter — wähle eine Vorlage oder gib unten ein eigenes Stichwort ein.</p>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addKeyword(keywordInput);
                        setKeywordInput('');
                      }
                    }}
                    disabled={readOnly}
                    placeholder="Eigenes Stichwort hinzufügen…"
                    className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => { addKeyword(keywordInput); setKeywordInput(''); }}
                    disabled={readOnly || !keywordInput.trim()}
                    className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                  >
                    Hinzufügen
                  </button>
                </div>

                <div>
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Gewerk-Vorlagen</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(NOTDIENST_KEYWORD_TEMPLATES).map(([key, tpl]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => applyTemplate(key)}
                        disabled={readOnly}
                        className="px-3 py-1.5 text-xs bg-white border border-border rounded-md hover:border-primary/50 hover:bg-primary/5 transition disabled:opacity-50"
                        title={`Fügt ${tpl.keywords.length} Stichwörter hinzu`}
                      >
                        + {tpl.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Aufpreis-Hinweis */}
              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="text-sm font-semibold">Aufpreis-Hinweis</h4>
                <ToggleRow
                  label="Aufpreis-Hinweis ansagen"
                  description={`${agentNameCap} sagt im Notdienst den Aufpreis-Hinweis vor der Weiterleitung an.`}
                  checked={aufpreisAktiv}
                  onChange={(val) => persistField('notdienstAufpreisHinweisAktiv', val)}
                  disabled={readOnly}
                />
                {aufpreisAktiv && (
                  <div>
                    <label className="text-sm font-medium text-text block mb-1">Hinweistext</label>
                    <textarea
                      value={aufpreisDraft}
                      onChange={(e) => setAufpreisDraft(e.target.value)}
                      onBlur={() => {
                        const trimmed = aufpreisDraft.trim();
                        if (trimmed !== (settings.notdienstAufpreisHinweisText || '').trim()) {
                          persistField('notdienstAufpreisHinweisText', trimmed || null);
                        }
                      }}
                      disabled={readOnly}
                      rows={2}
                      maxLength={500}
                      placeholder={NOTDIENST_DEFAULT_AUFPREIS_TEXT}
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-text-muted">
                        Wird im Gespräch wörtlich von {agentName} vorgelesen, bevor die Weiterleitung angeboten wird.
                      </p>
                      {!aufpreisDraft.trim() && (
                        <button
                          type="button"
                          onClick={() => {
                            setAufpreisDraft(NOTDIENST_DEFAULT_AUFPREIS_TEXT);
                            persistField('notdienstAufpreisHinweisText', NOTDIENST_DEFAULT_AUFPREIS_TEXT);
                          }}
                          disabled={readOnly}
                          className="text-xs text-primary hover:underline whitespace-nowrap"
                        >
                          Vorschlag einfügen
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Collapsible>
    </div>
  );
}

function EmailTagInput({ emails, onChange, disabled, isHeykikiOrg = false }) {
  const [input, setInput] = useState('');
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleAdd = () => {
    const email = input.trim().toLowerCase();
    if (!email) return;
    if (!emailRegex.test(email)) {
      toast.error('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }
    if (emails.includes(email)) {
      toast.error('Diese E-Mail-Adresse ist bereits vorhanden');
      return;
    }
    onChange([...emails, email]);
    setInput('');
  };

  return (
    <div>
      <label className="block text-sm font-medium text-text mb-1">E-Mail-Adressen</label>
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {emails.map((email) => (
            <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-200">
              <Mail className="w-3.5 h-3.5" />
              {email}
              {!disabled && (
                <button
                  onClick={() => onChange(emails.filter(e => e !== email))}
                  className="text-blue-400 hover:text-red-500 ml-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && (
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="z.B. chef@meinefirma.de"
            className="flex-1 px-3 py-2 text-sm border border-border rounded-lg"
          />
          <button
            onClick={handleAdd}
            disabled={!input.trim()}
            className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      )}
      <p className="text-xs text-text-muted mt-1.5">
        {isHeykikiOrg
          ? 'Alle Kiki-Benachrichtigungen (Terminanfragen, Rückrufe, KVAs, Beschwerden etc.) werden an diese Adressen weitergeleitet.'
          : 'Alle KI-Benachrichtigungen (Terminanfragen, Rückrufe, KVAs, Beschwerden etc.) werden an diese Adressen weitergeleitet.'}
      </p>
    </div>
  );
}

function AddItemInput({ placeholder, onAdd }) {
  const [value, setValue] = useState('');
  return (
    <div className="flex items-center gap-2 mt-2">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            onAdd(value.trim());
            setValue('');
          }
        }}
        className="flex-1 px-3 py-2 text-sm border border-border rounded-lg"
      />
      <button
        onClick={() => {
          if (value.trim()) {
            onAdd(value.trim());
            setValue('');
          }
        }}
        className="p-2 bg-primary text-white rounded-lg hover:bg-primary/90"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

// ============================================================
// HeyKiki-Pflichtfelder-Editor
// ============================================================
// - „Anliegen/Problem" (concern/anliegen/problem/beschreibung) als Header-
//   Zeile oben, locked: wird immer in Schritt 1 vertieft, nicht editierbar.
// - „Name" als erste Zeile der Drag-Liste, locked: Schema-hart erforderlich
//   für bookAppointment/leaveMessage — Pflicht erzwungen, nicht löschbar,
//   nicht verschiebbar.
// - Alle anderen Felder frei per Drag-and-Drop (framer-motion Reorder),
//   Pflicht/Optional toggelbar, löschbar.
// - onChange speichert das rebuildete Pflichtfelder-Objekt mit frischen
//   `order`-Werten (1, 2, 3, …) je Eintrag. Backend-Render sortiert danach.

const CONCERN_KEYS = new Set(['concern', 'anliegen', 'problem', 'beschreibung']);
const PHONE_KEYS = new Set(['phone', 'telefon']);
const KUNDENNUMMER_KEYS = new Set(['kundennummer', 'kundennr']);
// Adress-Identifikation ist ein Verhaltens-Flag, kein Stammdaten-Feld — der
// Eintrag in pflichtfelder steuert nur, ob Kiki im Anruf Adressen mit dem
// Bestand abgleicht. Unterscheidet sich vom regulären `adresse`-Pflichtfeld
// (Stammdaten-Sammlung) eindeutig über `modus: "identifikation"` im Backend.
const ADRESSE_IDENTIFIKATION_KEYS = new Set(['adresseidentifikation']);

function getFallbackOrder(lowerKey) {
  const FALLBACK = { name: 10, kundennummer: 15, phone: 20, telefon: 20, address: 30, adresse: 30, email: 40 };
  return FALLBACK[lowerKey] ?? 999;
}

function HeyKikiPflichtfelderEditor({ pflichtfelder, onChange, readOnly, isHeykikiOrg = true }) {
  // Voice-only-Sonderblöcke (Kundennummer-/Adress-Identifikation „im Anruf")
  // gelten nur für HeyKiki-Telefonie. Standalone (WhatsApp/E-Mail) bekommt
  // denselben Leitfaden ohne diese Voice-Slots; agentLabel hält die übrige
  // Microcopy org-korrekt, ohne die HeyKiki-Ansicht zu verändern.
  const agentLabel = isHeykikiOrg ? 'Kiki' : 'der KI-Agent';
  // Einträge trennen: concern-Header, Name-Lock, frei sortierbar.
  const entries = Object.entries(pflichtfelder || {});

  let concernEntry = null;
  let nameEntry = null;
  let phoneEntry = null;
  let kundennummerEntry = null;
  let adresseIdentifikationEntry = null;
  const freeEntries = [];
  for (const [key, cfg] of entries) {
    const lk = key.toLowerCase();
    if (CONCERN_KEYS.has(lk)) {
      if (!concernEntry) concernEntry = [key, cfg];
      continue;
    }
    if (lk === 'name') {
      nameEntry = [key, cfg];
      continue;
    }
    if (KUNDENNUMMER_KEYS.has(lk)) {
      if (!kundennummerEntry) kundennummerEntry = [key, cfg];
      continue;
    }
    if (ADRESSE_IDENTIFIKATION_KEYS.has(lk)) {
      if (!adresseIdentifikationEntry) adresseIdentifikationEntry = [key, cfg];
      continue;
    }
    if (PHONE_KEYS.has(lk)) {
      if (!phoneEntry) phoneEntry = [key, cfg];
      continue;
    }
    freeEntries.push([key, cfg]);
  }

  // Frei-Einträge nach `order` sortieren (Fallback: Standard-Prioritäten).
  freeEntries.sort(([kA, a], [kB, b]) => {
    const oA = typeof a?.order === 'number' ? a.order : getFallbackOrder(kA.toLowerCase());
    const oB = typeof b?.order === 'number' ? b.order : getFallbackOrder(kB.toLowerCase());
    return oA - oB;
  });

  // Für Reorder.Group brauchen wir ein stabiles Array als `values`. Wir nutzen
  // die Keys als Identifier.
  const freeKeys = freeEntries.map(([k]) => k);

  // Drag-State: lokale Kopie der Reihenfolge während des Ziehens.
  // `onReorder` feuert auf jedem Zwischenschritt — wir aktualisieren dort
  // localKeys sofort (damit der Drag visuell flüssig ist) und debouncen den
  // Save um 400 ms. Nach dem Debounce prüfen wir per Signatur-Vergleich, ob
  // die Reihenfolge sich seit dem letzten Save geändert hat — nur dann feuert
  // `onChange` (und damit der Toast). Debounce deckt Drop-Timing zuverlässig
  // ab (anders als `onDragEnd`, das bei Reorder-Drags nicht immer sauber
  // feuert).
  const [localKeys, setLocalKeys] = useState(freeKeys);
  const savedKeysRef = useRef(freeKeys.join('|'));
  const saveTimerRef = useRef(null);
  const freeKeysSig = freeKeys.join('|');

  useEffect(() => {
    // Wenn sich die Quell-Daten extern ändern (pflicht-Toggle, Delete, Reset),
    // ziehen wir localKeys und den savedKeysRef nach.
    setLocalKeys(freeKeys);
    savedKeysRef.current = freeKeysSig;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeKeysSig]);

  // Timer aufräumen beim Unmount
  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  function rebuild({ newFreeKeys, nameCfg, phoneCfg, kundennummerCfg, adresseIdentifikationCfg, concernKeyValue, mutations = {} }) {
    // Baut ein frisches pflichtfelder-Objekt mit aktualisierten `order`-Werten.
    // Kundennummer (optional, erster Lock-Slot) → 5, Adress-Identifikation
    // (optional, Verhaltens-Flag) → 7, Name → 10, Telefonnummer → 20 (alle
    // gelockt; Name + Phone Pflicht erzwungen, Kundennummer/Adress-
    // Identifikation immer optional). Concern bleibt order=999
    // (EXCLUDE_FROM_STEP2).
    // `mutations`: { [key]: { patch-object } } — z.B. Pflicht-Toggle.
    const out = {};
    // 1) Kundennummer-Slot (HeyKiki-Identifikation) — opt-in über
    // handleAddKundennummer / -Remove. Pflicht ist immer false (Neukunden
    // haben keine Bestandsnummer); Order=5 platziert das Feld VOR Name
    // im Leitfaden — eine genannte Kundennummer liefert Name/Telefon/
    // Adresse direkt mit, sodass alle weiteren Stammdaten-Fragen
    // unnötig werden.
    if (kundennummerCfg) {
      out.kundennummer = { ...kundennummerCfg, ...(mutations.kundennummer || {}), label: 'Kundennummer', pflicht: false, order: 5 };
    }
    // 2) Adress-Identifikations-Slot — opt-in über
    // handleAddAdresseIdentifikation / -Remove. `modus: "identifikation"`
    // unterscheidet diesen Verhaltens-Flag vom regulären `adresse`-Pflichtfeld
    // (Stammdaten-Sammlung); beide können unabhängig aktiv sein. Order=7
    // platziert den Eintrag zwischen Kundennummer und Name; ausgeblendet aus
    // dem Field-Listing in Schritt 2 (EXCLUDE_FROM_STEP2). Steuert nur das
    // Sonderfall-Block-Rendering im Leitfaden — Kiki fragt im Anruf nach
    // Strasse+Hausnummer und gleicht mit dem Bestand ab.
    if (adresseIdentifikationCfg) {
      out.adresseIdentifikation = {
        ...adresseIdentifikationCfg,
        ...(mutations.adresseIdentifikation || {}),
        label: 'Adresse',
        modus: 'identifikation',
        pflicht: false,
        order: 7
      };
    }
    // 3) Name lock (Schema-hart benötigt)
    if (nameCfg) {
      out.name = { ...nameCfg, ...(mutations.name || {}), label: 'Name', pflicht: true, order: 10 };
    }
    // 4) Telefonnummer lock (Fallback für anonyme Caller-ID / Weiterleitung)
    if (phoneCfg) {
      // Key aus der DB übernehmen (könnte „phone" oder „telefon" sein) —
      // ansonsten kanonisch „phone" verwenden.
      const phoneKey = (phoneEntry && phoneEntry[0]) || 'phone';
      out[phoneKey] = { ...phoneCfg, ...(mutations[phoneKey] || {}), label: 'Telefonnummer', pflicht: true, order: 20 };
    }
    // 5) Frei-Einträge in gewünschter Reihenfolge
    let orderCounter = 100;
    for (const key of newFreeKeys) {
      const existing = pflichtfelder[key];
      if (!existing) continue;
      out[key] = { ...existing, ...(mutations[key] || {}), order: orderCounter };
      orderCounter += 10;
    }
    // 6) Concern zuletzt (gehört nicht in Schritt 2, aber muss in pflichtfelder bleiben)
    if (concernEntry) {
      const [ck, cc] = concernEntry;
      const ckTarget = concernKeyValue?.key || ck;
      out[ckTarget] = { ...cc, ...(mutations[ckTarget] || {}), pflicht: true, order: 999 };
    }
    return out;
  }

  function handleReorder(newOrderedKeys) {
    if (readOnly) return;
    setLocalKeys(newOrderedKeys);
    // Debounce: pendelt beim Drag mit jedem Reorder-Event zurück, nur der
    // letzte Zustand (400 ms nach dem letzten Event) triggert den Save.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const currentSig = newOrderedKeys.join('|');
      if (currentSig === savedKeysRef.current) return; // Reihenfolge unverändert → kein Save
      savedKeysRef.current = currentSig;
      const updated = rebuild({
        newFreeKeys: newOrderedKeys,
        nameCfg: nameEntry?.[1],
        phoneCfg: phoneEntry?.[1],
        kundennummerCfg: kundennummerEntry?.[1],
      });
      onChange(updated);
    }, 400);
  }

  function handleTogglePflicht(key, newValue) {
    if (readOnly) return;
    const updated = rebuild({
      newFreeKeys: freeKeys,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: kundennummerEntry?.[1],
      adresseIdentifikationCfg: adresseIdentifikationEntry?.[1],
      mutations: { [key]: { pflicht: newValue } },
    });
    onChange(updated);
  }

  function handleDelete(key) {
    if (readOnly) return;
    const filtered = freeKeys.filter(k => k !== key);
    const updated = rebuild({
      newFreeKeys: filtered,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: kundennummerEntry?.[1],
      adresseIdentifikationCfg: adresseIdentifikationEntry?.[1],
    });
    onChange(updated);
  }

  function handleAddKundennummer() {
    if (readOnly) return;
    const updated = rebuild({
      newFreeKeys: freeKeys,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: { label: 'Kundennummer', pflicht: false, order: 5 },
      adresseIdentifikationCfg: adresseIdentifikationEntry?.[1],
    });
    onChange(updated);
  }

  function handleRemoveKundennummer() {
    if (readOnly) return;
    const updated = rebuild({
      newFreeKeys: freeKeys,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: null,
      adresseIdentifikationCfg: adresseIdentifikationEntry?.[1],
    });
    onChange(updated);
  }

  function handleAddAdresseIdentifikation() {
    if (readOnly) return;
    const updated = rebuild({
      newFreeKeys: freeKeys,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: kundennummerEntry?.[1],
      adresseIdentifikationCfg: { label: 'Adresse', modus: 'identifikation', pflicht: false, order: 7 },
    });
    onChange(updated);
  }

  function handleRemoveAdresseIdentifikation() {
    if (readOnly) return;
    const updated = rebuild({
      newFreeKeys: freeKeys,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: kundennummerEntry?.[1],
      adresseIdentifikationCfg: null,
    });
    onChange(updated);
  }

  // Concern-Freitext: Admin trägt firmenspezifischen Prompt-Zusatz ein, der in
  // Schritt 1 des ElevenLabs-Leitfadens injiziert wird (siehe agentConfigService).
  // Quelle of truth: pflichtfelder.concern.anweisung (string oder fehlt).
  const savedConcernAnweisung = (concernEntry?.[1]?.anweisung || '').trim();
  const [concernExpanded, setConcernExpanded] = useState(false);
  const [concernDraft, setConcernDraft] = useState(savedConcernAnweisung);
  const concernSavedRef = useRef(savedConcernAnweisung);
  useEffect(() => {
    // Sync wenn sich pflichtfelder extern ändert (Reset, anderer Save, …)
    if (savedConcernAnweisung !== concernSavedRef.current) {
      concernSavedRef.current = savedConcernAnweisung;
      setConcernDraft(savedConcernAnweisung);
    }
  }, [savedConcernAnweisung]);

  const concernDirty = concernDraft.trim() !== savedConcernAnweisung;

  function persistConcernAnweisung(nextValue) {
    // Baut pflichtfelder neu via rebuild() und überschreibt anschließend das
    // anweisung-Feld auf der concern-Entry. Leerstring → Feld entfernen, damit
    // der Backend-Block nicht renderbar wird und die DB nicht mit „" zugemüllt
    // ist. nextValue === null bedeutet explizites Löschen.
    if (readOnly) return;
    const trimmed = (nextValue ?? '').trim();
    const updated = rebuild({
      newFreeKeys: freeKeys,
      nameCfg: nameEntry?.[1],
      phoneCfg: phoneEntry?.[1],
      kundennummerCfg: kundennummerEntry?.[1],
      adresseIdentifikationCfg: adresseIdentifikationEntry?.[1],
    });
    if (concernEntry) {
      const ck = concernEntry[0];
      const next = { ...(updated[ck] || concernEntry[1]) };
      if (trimmed) {
        next.anweisung = trimmed;
      } else {
        delete next.anweisung;
      }
      updated[ck] = next;
    } else if (trimmed) {
      // Kein concern-Eintrag vorhanden → minimal anlegen.
      updated.concern = { label: 'Anliegen/Problem', pflicht: true, order: 999, anweisung: trimmed };
    }
    onChange(updated);
  }

  function handleConcernSave() {
    persistConcernAnweisung(concernDraft);
    setConcernExpanded(false);
  }

  function handleConcernDelete() {
    setConcernDraft('');
    persistConcernAnweisung('');
    setConcernExpanded(false);
  }

  function handleConcernCancel() {
    setConcernDraft(savedConcernAnweisung);
    setConcernExpanded(false);
  }

  const concernTeaser = savedConcernAnweisung
    ? (savedConcernAnweisung.length > 110
        ? savedConcernAnweisung.slice(0, 110).replace(/\s+\S*$/, '') + '…'
        : savedConcernAnweisung)
    : null;

  return (
    <div className="space-y-2">
      {/* Concern / Anliegen-Header — klickbar, mit optionalem Firmen-Freitext.
          Der Freitext wird vom Backend (agentConfigService) in Schritt 1 des
          ElevenLabs-Leitfadens als „Firmenspezifische Hinweise zum Anliegen"
          eingefügt. Leer = Standard-Verhalten wie zuvor. */}
      <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50/50">
        <button
          type="button"
          onClick={() => setConcernExpanded(v => !v)}
          className="w-full flex items-center justify-between p-3 text-left cursor-pointer hover:bg-blue-50"
          aria-expanded={concernExpanded}
        >
          <div className="flex items-center gap-3 min-w-0">
            <MessageSquare className="w-4 h-4 text-blue-700 flex-shrink-0" />
            <div className="min-w-0">
              <span className="text-sm font-medium text-blue-900">Anliegen / Problem</span>
              {concernTeaser ? (
                <p className="text-xs text-blue-800/80 mt-0.5 truncate">{concernTeaser}</p>
              ) : (
                <p className="text-xs text-blue-700/80 mt-0.5">
                  Klicken, um firmenspezifische Hinweise hinzuzufügen — {agentLabel} vertieft das Anliegen damit in Schritt 1.
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">Schritt 1</span>
            {concernExpanded ? <ChevronUp className="w-4 h-4 text-blue-700" /> : <ChevronDown className="w-4 h-4 text-blue-700" />}
          </div>
        </button>
        <Collapsible isOpen={concernExpanded}>
          <div className="px-3 pb-3 pt-0 space-y-2">
            <p className="text-xs text-blue-800/80">
              Schreibe als kurzen Prompt, was {agentLabel} bei jeder Anfrage zusätzlich erfragen soll
              — z.B. Geräte-Typ, Baujahr, Modellnummer, Foto-Zusendung. Der Text wird in den
              Leitfaden des Agenten injiziert. Leer lassen = Standard-Verhalten.
            </p>
            <textarea
              value={concernDraft}
              onChange={(e) => setConcernDraft(e.target.value)}
              readOnly={readOnly}
              rows={5}
              placeholder={'Z.B.: Bei Heizungsstörungen IMMER nach Hersteller, Baujahr und Fehleranzeige am Display fragen. Bitte den Anrufer, ein Foto vom Manometer und vom Typenschild per WhatsApp zu senden.'}
              className={`w-full px-3 py-2 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/40 resize-y ${readOnly ? 'bg-blue-50/40 cursor-default' : ''}`}
            />
            {!readOnly && (
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleConcernSave}
                    disabled={!concernDirty}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Speichern
                  </button>
                  <button
                    type="button"
                    onClick={handleConcernCancel}
                    className="px-3 py-1.5 text-xs font-medium text-text-muted hover:text-text"
                  >
                    Abbrechen
                  </button>
                </div>
                {savedConcernAnweisung && (
                  <button
                    type="button"
                    onClick={handleConcernDelete}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:text-red-700"
                    title={`Freitext entfernen — ${agentLabel} nutzt wieder den Standard-Leitfaden`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Freitext entfernen
                  </button>
                )}
              </div>
            )}
          </div>
        </Collapsible>
      </div>

      {/* Kundennummer-Slot — erste Zeile (vor Name, order=5).
          Opt-in: aktiv = Eintrag in pflichtfelder, deaktiv = Eintrag fehlt.
          Pflicht-Häkchen ausgegraut: Neukunden haben keine Bestandsnummer,
          das Feld ist immer optional. Klick auf X deaktiviert das Feature
          komplett — Kiki fragt im Anruf nicht mehr danach (Flow wie ohne
          Kundennummer-Identifikation). Steht bewusst VOR Name: eine
          genannte Kundennummer liefert Name/Telefon/Adresse direkt mit
          und macht alle weiteren Stammdaten-Fragen unnötig.
          Wenn das Feature deaktiviert ist, sitzt der Aktivieren-Button
          an derselben Stelle (zwischen Anliegen/Problem und Name) — so
          bleibt das Layout konsistent. */}
      {!kundennummerEntry && !readOnly && (
        <button
          type="button"
          onClick={handleAddKundennummer}
          className="flex items-center gap-2 px-3 py-2 text-sm text-primary border border-dashed border-primary/40 rounded-lg hover:bg-primary/5 w-full justify-center"
          title={`Im Anruf fragt ${agentLabel} nach der Kundennummer und gleicht sie mit dem Bestand ab. Optional — bei Deaktivierung läuft der bisherige Flow ohne Änderung.`}
        >
          <Plus className="w-4 h-4" />
          Kundennummer-Identifikation aktivieren
        </button>
      )}
      {kundennummerEntry && (
        <div className="p-3 rounded-lg border border-border bg-blue-50/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs" title="Identifikation per Kundennummer — Kiki fragt im Anruf danach, wenn die Caller-ID nicht erkannt wurde">🔒</span>
              <span className="text-sm font-medium">Kundennummer</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">aktiv</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <label
                className="flex items-center gap-2 opacity-50 cursor-not-allowed"
                title="Optional — Neukunden ohne Bestandsnummer können trotzdem identifiziert werden."
              >
                <input type="checkbox" disabled className="accent-primary" />
                <span className="text-xs text-text-muted">Pflicht</span>
              </label>
              {!readOnly && (
                <button
                  onClick={handleRemoveKundennummer}
                  className="text-red-400 hover:text-red-600"
                  title={`Kundennummer-Identifikation deaktivieren — ${agentLabel} fragt im Anruf nicht mehr danach`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-text-muted leading-relaxed">
            Im Anruf fragt {agentLabel} zuerst nach der Kundennummer und gleicht sie mit dem Bestand ab.
            Bei Treffer werden Name, Telefon und Adresse aus den Stammdaten übernommen — {agentLabel}
            verifiziert nur kurz den Nachnamen und überspringt die restliche Datenaufnahme.
            Bestandskunden, die schon per Anrufer-Nummer erkannt wurden, werden nicht erneut
            gefragt. Bei unbekannter oder falsch genannter Kundennummer läuft die normale
            Stammdaten-Aufnahme weiter; die genannte Nummer wird nicht beim Neukunden gespeichert.
          </p>
        </div>
      )}

      {/* Adress-Identifikations-Slot — order=7, sitzt zwischen Kundennummer
          und Name. Opt-in: aktiv = Eintrag in pflichtfelder mit
          modus:"identifikation", deaktiv = Eintrag fehlt. Unabhängig vom
          regulären Adresse-Pflichtfeld (Stammdaten-Sammlung) — das bleibt
          weiter unten frei sortierbar. */}
      {!adresseIdentifikationEntry && !readOnly && (
        <button
          type="button"
          onClick={handleAddAdresseIdentifikation}
          className="flex items-center gap-2 px-3 py-2 text-sm text-primary border border-dashed border-primary/40 rounded-lg hover:bg-primary/5 w-full justify-center"
          title={`Im Anruf fragt ${agentLabel} nach Strasse und Hausnummer und gleicht die Adresse mit dem Bestand ab. Optional — fängt Bestandskunden ab, die weder per Caller-ID noch per Kundennummer erkannt werden.`}
        >
          <Plus className="w-4 h-4" />
          Adress-Identifikation aktivieren
        </button>
      )}
      {adresseIdentifikationEntry && (
        <div className="p-3 rounded-lg border border-border bg-blue-50/40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs" title="Identifikation per Adresse — Kiki fragt im Anruf nach Strasse + Hausnummer und gleicht mit dem Bestand ab">🔒</span>
              <span className="text-sm font-medium">Adresse</span>
              <span className="text-xs text-text-muted">— Identifikation</span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">aktiv</span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <label
                className="flex items-center gap-2 opacity-50 cursor-not-allowed"
                title="Optional — Verhaltens-Flag, kein Pflichtfeld."
              >
                <input type="checkbox" disabled className="accent-primary" />
                <span className="text-xs text-text-muted">Pflicht</span>
              </label>
              {!readOnly && (
                <button
                  onClick={handleRemoveAdresseIdentifikation}
                  className="text-red-400 hover:text-red-600"
                  title={`Adress-Identifikation deaktivieren — ${agentLabel} nutzt die Adresse nicht mehr zur Identifikation (das normale Adress-Pflichtfeld bleibt davon unberührt)`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-text-muted leading-relaxed">
            Über die Adresse kann {agentLabel} Bestandskunden zusätzlich finden, wenn weder Caller-ID
            noch Kundennummer einen Treffer ergeben. Bei einem oder mehreren möglichen
            Treffern fragt {agentLabel} zur Sicherheit den Nachnamen ab, bevor die Stammdaten als
            bestätigt gelten — schützt vor Verwechslungen in Mehrfamilienhäusern und bei
            Tippfehlern. Das normale Adress-Pflichtfeld weiter unten bleibt davon unberührt.
          </p>
        </div>
      )}

      {/* Name-Lock — zweite Zeile (nach Kundennummer, vor Telefon), Schema-Zwang */}
      {nameEntry && (
        <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-gray-50">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-xs" title="Systemfeld — für Tool-Aufrufe benötigt">🔒</span>
            <span className="text-sm font-medium">{nameEntry[1].label || 'Name'}</span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <label className="flex items-center gap-2 opacity-50 cursor-not-allowed">
              <input type="checkbox" checked disabled className="accent-primary" />
              <span className="text-xs text-text-muted">Pflicht</span>
            </label>
          </div>
        </div>
      )}

      {/* Telefonnummer-Lock — dritte Zeile, Fallback für anonyme Caller-ID
          oder weitergeleitete Anrufe. Caller-ID deckt den Normalfall ab,
          Kiki fragt nach wenn die Nummer nicht automatisch erkannt wurde. */}
      {phoneEntry && (
        <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-gray-50">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-xs" title="Systemfeld — Fallback für anonyme oder weitergeleitete Anrufe">🔒</span>
            <span className="text-sm font-medium">{phoneEntry[1].label || 'Telefonnummer'}</span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <label className="flex items-center gap-2 opacity-50 cursor-not-allowed">
              <input type="checkbox" checked disabled className="accent-primary" />
              <span className="text-xs text-text-muted">Pflicht</span>
            </label>
          </div>
        </div>
      )}

      {/* Frei sortierbare Stammdaten-Felder */}
      <Reorder.Group
        axis="y"
        values={localKeys}
        onReorder={handleReorder}
        className="space-y-2"
      >
        {localKeys.map((key) => {
          const field = pflichtfelder[key] || {};
          return (
            <Reorder.Item
              key={key}
              value={key}
              dragListener={!readOnly}
              className="flex items-center justify-between p-3 rounded-lg border border-border bg-white cursor-grab active:cursor-grabbing"
              whileDrag={{ scale: 1.02, boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                <span className="text-sm font-medium">{field.label || key}</span>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <label
                  className="flex items-center gap-2 cursor-pointer"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={field.pflicht !== false}
                    onChange={(e) => handleTogglePflicht(key, e.target.checked)}
                    disabled={readOnly}
                    className="accent-primary"
                  />
                  <span className="text-xs text-text-muted">Pflicht</span>
                </label>
                {!readOnly && (
                  <button
                    onClick={() => handleDelete(key)}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
    </div>
  );
}
